<template>
  <div class="sbs">
    <div v-if="loading" class="sbs-loading"><div class="sbs-spinner"></div></div>
    <div v-else-if="loadError" class="sbs-error">
      <p class="sbs-error-title">Could not load billing</p>
      <p class="sbs-error-msg">{{ loadError }}</p>
      <button class="sbs-btn sbs-btn-outline" @click="fetchAll">Retry</button>
    </div>

    <div v-else class="sbs-inner">
      <header class="sbs-header">
        <h1 class="sbs-title">Subscription &amp; Billing</h1>
        <p class="sbs-subtitle">{{ billingPageSubtitle }}</p>
      </header>

      <nav class="sbs-tabs" role="tablist">
        <button type="button" role="tab" :aria-selected="activeTab === 'plans'" :class="['sbs-tab', { active: activeTab === 'plans' }]" @click="activeTab = 'plans'">Pricing &amp; Plans</button>
        <button v-if="showUsageTab" type="button" role="tab" :aria-selected="activeTab === 'usage'" :class="['sbs-tab', { active: activeTab === 'usage' }]" @click="activeTab = 'usage'">Usage</button>
        <button type="button" role="tab" :aria-selected="activeTab === 'history'" :class="['sbs-tab', { active: activeTab === 'history' }]" @click="activeTab = 'history'">Billing History</button>
      </nav>

      <!-- Pricing & Plans -->
      <div v-show="activeTab === 'plans'" class="sbs-panel">
        <!-- ——— Variant: new (Starter / Pro) ——— -->
        <template v-if="billingVariant === 'new'">
          <div v-if="isPaymentPastDue" class="sbs-trial-block sbs-trial-block-expired">
            <div class="sbs-trial-hd">
              <div class="sbs-trial-hd-left">
                <div class="sbs-trial-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                </div>
                <div>
                  <span class="sbs-trial-badge sbs-trial-badge-expired">Payment failed</span>
                  <div class="sbs-trial-title">Your last payment didn't go through</div>
                </div>
              </div>
            </div>
            <div class="sbs-trial-footnote">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span>Update your payment method below to restore billing and keep your alerts running. Your plan and data are unchanged.</span>
            </div>
            <button v-if="canManageBilling" type="button" class="sbs-btn sbs-btn-upgrade sbs-past-due-cta" @click="scrollToPaymentMethod">Update payment method →</button>
            <p v-else class="sbs-member-admin-hint">Ask your account admin to update the payment method on file.</p>
          </div>

          <div v-else-if="isTrialBillingExpired" class="sbs-trial-block sbs-trial-block-expired">
            <div class="sbs-trial-hd">
              <div class="sbs-trial-hd-left">
                <div class="sbs-trial-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                </div>
                <div>
                  <span class="sbs-trial-badge sbs-trial-badge-expired">Trial Ended</span>
                  <div class="sbs-trial-title">Your free trial has ended</div>
                </div>
              </div>
              <div class="sbs-trial-days">
                <div class="sbs-trial-days-num">0</div>
                <div class="sbs-trial-days-lbl">Days remaining</div>
              </div>
            </div>

            <div class="sbs-trial-progress-wrap">
              <div class="sbs-trial-progress-meta">
                <span>Trial completed</span>
                <span>Ended {{ nextBillingDateDisplay }}</span>
              </div>
              <div class="sbs-trial-progress">
                <div class="sbs-trial-progress-fill" style="width: 100%;"></div>
              </div>
            </div>

            <div class="sbs-trial-stats">
              <div class="sbs-trial-stat">
                <div class="sbs-trial-stat-lbl">ASINs monitored</div>
                <div class="sbs-trial-stat-val">{{ summaryAsinsDisplay }}</div>
                <div class="sbs-trial-stat-sub">During your trial</div>
              </div>
              <div class="sbs-trial-stat">
                <div class="sbs-trial-stat-lbl">Changes caught</div>
                <div class="sbs-trial-stat-val">{{ orderVolumeChangesCaughtDisplay }}</div>
                <div class="sbs-trial-stat-sub">In the last 7 days</div>
              </div>
              <div class="sbs-trial-stat">
                <div class="sbs-trial-stat-lbl">{{ trialPlanChosen ? 'Your plan' : 'Recommended plan' }}</div>
                <div class="sbs-trial-stat-val">{{ trialPlanChosen ? planAfterTrialLabel : 'Pro' }}</div>
                <div class="sbs-trial-stat-sub">{{ canManageBilling ? (trialPlanChosen ? 'Add a payment method to resume' : 'Pick a plan below to resume') : 'Ask your admin to choose a plan' }}</div>
              </div>
            </div>

            <div class="sbs-trial-footnote">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span>{{ canManageBilling ? 'Your alerts are paused. Pick a plan below to resume tracking — your data is safe.' : 'Your alerts are paused. Ask your account admin to choose a plan — your data is safe.' }}</span>
            </div>
          </div>

          <div v-else-if="isTrialBillingActive" class="sbs-trial-block">
            <div class="sbs-trial-hd">
              <div class="sbs-trial-hd-left">
                <div class="sbs-trial-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div>
                  <span class="sbs-trial-badge"><span class="sbs-trial-pulse-dot"></span>Trial Active</span>
                  <div class="sbs-trial-title">You're on a free {{ trialLengthDays }}-day trial</div>
                </div>
              </div>
              <div class="sbs-trial-days">
                <div class="sbs-trial-days-num">{{ trialDaysRemainingDisplay }}</div>
                <div class="sbs-trial-days-lbl">Days remaining</div>
              </div>
            </div>

            <div class="sbs-trial-progress-wrap">
              <div class="sbs-trial-progress-meta">
                <span>{{ trialDayOfDisplay }}</span>
                <span>Trial ends {{ nextBillingDateDisplay }}</span>
              </div>
              <div class="sbs-trial-progress">
                <div class="sbs-trial-progress-fill" :style="{ width: trialProgressWidth }"></div>
              </div>
            </div>

            <div class="sbs-trial-stats">
              <div class="sbs-trial-stat">
                <div class="sbs-trial-stat-lbl">ASINs monitored</div>
                <div class="sbs-trial-stat-val">{{ summaryAsinsDisplay }}</div>
              </div>
              <div class="sbs-trial-stat">
                <div class="sbs-trial-stat-lbl">Changes caught</div>
                <div class="sbs-trial-stat-val">{{ orderVolumeChangesCaughtDisplay }}</div>
                <div class="sbs-trial-stat-sub">In the last 7 days</div>
              </div>
              <div class="sbs-trial-stat">
                <div class="sbs-trial-stat-lbl">Plan after trial</div>
                <div class="sbs-trial-stat-val">{{ planAfterTrialLabel }}</div>
                <div class="sbs-trial-stat-sub">{{ planAfterTrialSub }}</div>
              </div>
            </div>

            <div class="sbs-trial-footnote">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span>{{ canManageBilling ? 'Track as many orders as you need during your trial. Pick a plan below — you can change it any time before your trial ends.' : 'Track as many orders as you need during your trial. Your admin manages plan selection for this account.' }}</span>
            </div>
          </div>

          <div v-else class="sbs-current-plan">
            <div class="sbs-current-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="sbs-current-main">
              <span class="sbs-current-name">{{ currentPlanTitle }}</span>
              <span class="sbs-current-meta">{{ currentPlanSubtitle }}</span>
            </div>
            <div class="sbs-current-next">
              <span class="sbs-current-next-label">Next billing</span>
              <span class="sbs-current-next-date">{{ nextBillingDateDisplay }}</span>
            </div>
          </div>

          <template v-if="canManageBilling && !isFlatYearlyPlan">
          <h2 class="sbs-section-heading">{{ planSectionHeading }}</h2>
          <p class="sbs-plans-help">{{ planSectionHelp }}</p>
          <div :class="['sbs-plan-cards', { 'sbs-plan-cards-3': showProAnnualCard }]">
            <div :class="planCardClasses('starter')">
              <span v-if="planCard('starter').ribbon" :class="['sbs-plan-ribbon', 'sbs-plan-ribbon-' + planCard('starter').ribbonTone]">{{ planCard('starter').ribbon }}</span>
              <h3 class="sbs-plan-name">Starter</h3>
              <p class="sbs-plan-for">For brands on 1–2 storefronts</p>
              <p class="sbs-plan-price"><span class="sbs-plan-currency">$</span><span class="sbs-plan-amount">19</span><span class="sbs-plan-period">/month</span></p>
              <p class="sbs-plan-incl">2 storefronts included</p>
              <button
                type="button"
                :class="planButtonClasses('starter')"
                :disabled="planCard('starter').disabled"
                :title="planCard('starter').title"
                @click="onPlanAction('starter')"
              >{{ planCard('starter').label }}</button>
              <ul class="sbs-plan-features">
                <li>Unlimited ASINs &amp; all marketplaces</li>
                <li>2 storefronts included</li>
                <li>All alert types · real-time delivery</li>
                <li>COGS, floor price &amp; margin alerts</li>
              </ul>
              <div class="sbs-plan-footnote">Need more storefronts? Pro scales with you.</div>
            </div>

            <div :class="planCardClasses('pro')">
              <span v-if="planCard('pro').ribbon" :class="['sbs-plan-ribbon', 'sbs-plan-ribbon-' + planCard('pro').ribbonTone]">{{ planCard('pro').ribbon }}</span>
              <h3 class="sbs-plan-name">Pro</h3>
              <p class="sbs-plan-for">Scales with your storefronts</p>
              <p class="sbs-plan-price"><span class="sbs-plan-currency">$</span><span class="sbs-plan-amount">49</span><span class="sbs-plan-period">/month</span></p>
              <p class="sbs-plan-incl">5 storefronts included</p>
              <button
                type="button"
                :class="planButtonClasses('pro')"
                :disabled="planCard('pro').disabled"
                :title="planCard('pro').title"
                @click="onPlanAction('pro')"
              >{{ planCard('pro').label }}</button>
              <p class="sbs-plan-plus-label">EVERYTHING IN STARTER, PLUS</p>
              <ul class="sbs-plan-features sbs-plan-features-pro">
                <li>Competitor seller profiles</li>
                <li>Seller name, location &amp; storefront</li>
                <li><strong>AI Mode</strong> <span class="sbs-phase-pill">Phase 2</span></li>
              </ul>

              <!-- Storefront counter -->
              <div class="sbs-sf-counter">
                <div class="sbs-sf-counter-q">How many storefronts do you manage?</div>
                <div class="sbs-sf-counter-row">
                  <button type="button" class="sbs-sf-step" aria-label="Fewer storefronts" :disabled="proStorefrontCount <= 1" @click="proStorefrontCount = Math.max(1, proStorefrontCount - 1)">−</button>
                  <span class="sbs-sf-count">{{ proStorefrontCount }}</span>
                  <button type="button" class="sbs-sf-step" aria-label="More storefronts" :disabled="proStorefrontCount >= 120" @click="proStorefrontCount = Math.min(120, proStorefrontCount + 1)">+</button>
                </div>
                <div class="sbs-sf-hint">{{ proStorefrontHint }}</div>
                <div class="sbs-sf-total-box">
                  <span class="sbs-sf-total-label">Your Pro plan</span>
                  <span class="sbs-sf-total-price">${{ proStorefrontTotalPrice }}<span class="sbs-sf-total-period"> /month</span></span>
                </div>
                <div class="sbs-sf-save">↓ Every storefront you add costs less than the last</div>
                <button type="button" class="sbs-sf-vol-toggle" @click="showVolumePricing = !showVolumePricing">
                  View volume pricing {{ showVolumePricing ? '▴' : '▾' }}
                </button>
                <table v-if="showVolumePricing" class="sbs-sf-vol-table">
                  <thead><tr><th>Storefronts</th><th>Per storefront</th><th>Volume saving</th></tr></thead>
                  <tbody>
                    <tr><td>1–5</td><td>Included in $49</td><td>—</td></tr>
                    <tr><td>6–15</td><td>$19</td><td>—</td></tr>
                    <tr><td>16–30</td><td>$14</td><td class="sbs-sf-saving">~26% lower</td></tr>
                    <tr><td>31–60</td><td>$10</td><td class="sbs-sf-saving">~47% lower</td></tr>
                    <tr><td>61+</td><td>$7</td><td class="sbs-sf-saving">~63% lower</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="showProAnnualCard" :class="planCardClasses('pro_annual')">
              <span v-if="planCard('pro_annual').ribbon" :class="['sbs-plan-ribbon', 'sbs-plan-ribbon-' + planCard('pro_annual').ribbonTone]">{{ planCard('pro_annual').ribbon }}</span>
              <h3 class="sbs-plan-name">Pro Annual</h3>
              <p class="sbs-plan-for">Pro, paid once a year</p>
              <p class="sbs-plan-price"><span class="sbs-plan-currency">$</span><span class="sbs-plan-amount">{{ proAnnualPriceDisplay }}</span><span class="sbs-plan-period">/year</span></p>
              <p class="sbs-plan-incl">5 storefronts included · one invoice a year</p>
              <button
                type="button"
                :class="planButtonClasses('pro_annual')"
                :disabled="planCard('pro_annual').disabled"
                :title="planCard('pro_annual').title"
                @click="onPlanAction('pro_annual')"
              >{{ planCard('pro_annual').label }}</button>
              <p class="sbs-plan-plus-label">EVERYTHING IN PRO, PLUS</p>
              <ul class="sbs-plan-features sbs-plan-features-pro">
                <li>One payment covers the whole year</li>
                <li>No monthly card charges to track</li>
                <li>Extra storefronts billed yearly at the Pro rates</li>
              </ul>
              <div class="sbs-plan-footnote">{{ proAnnualFootnote }}</div>
            </div>
          </div>
          </template>
          <p v-else class="sbs-member-admin-hint">Plan changes are managed by your account admin.</p>

          <div v-if="!canManageBilling" class="sbs-your-plan-stats sbs-member-billing-stats">
            <div class="sbs-your-plan-stat">
              <span class="sbs-your-plan-stat-label">Last billed</span>
              <span class="sbs-your-plan-stat-value">${{ summaryLastBilledAmount }}</span>
              <span class="sbs-your-plan-stat-sub">{{ summaryLastBilledDetail }}</span>
            </div>
            <div class="sbs-your-plan-stat">
              <span class="sbs-your-plan-stat-label">Orders this month</span>
              <span class="sbs-your-plan-stat-value">{{ orderVolumeOrdersThisMonthDisplay }}</span>
              <span class="sbs-your-plan-stat-sub">{{ orderVolumeOrdersCompareLine }}</span>
            </div>
          </div>
        </template>
        <template v-else-if="billingVariant === 'legacy'">
          <div class="sbs-your-plan-card">
            <span class="sbs-your-plan-badge">Your plan</span>
            <div class="sbs-your-plan-head">
              <div class="sbs-your-plan-head-main">
                <h2 class="sbs-your-plan-title">{{ legacyPlanTitle }}</h2>
                <p class="sbs-your-plan-desc">{{ legacyPricingLine }}</p>
              </div>
              <div class="sbs-your-plan-next">
                <span class="sbs-your-plan-next-label">Next billing</span>
                <span class="sbs-your-plan-next-date">{{ nextBillingDateDisplay }}</span>
              </div>
            </div>
            <div class="sbs-your-plan-stats">
              <div class="sbs-your-plan-stat">
                <span class="sbs-your-plan-stat-label">Last billed</span>
                <span class="sbs-your-plan-stat-value">${{ summaryLastBilledAmount }}</span>
                <span class="sbs-your-plan-stat-sub">{{ summaryLastBilledDetail }}</span>
              </div>
              <div class="sbs-your-plan-stat">
                <span class="sbs-your-plan-stat-label">ASINs tracked</span>
                <span class="sbs-your-plan-stat-value">{{ summaryAsinsDisplay }}</span>
                <span class="sbs-your-plan-stat-sub">Across all marketplaces</span>
              </div>
            </div>
            <p class="sbs-your-plan-foot">{{ legacyFootnote }}</p>
          </div>
          <button v-if="canManageBilling" type="button" class="sbs-email-cta" @click="openPlanChangeModal">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a1.99 1.99 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
            Looking to change plans? Email us →
          </button>
        </template>

        <!-- ——— Variant: daily usage ——— -->
        <template v-else>
          <div v-if="isDailyUsageTrialExpiredNoPayment" class="sbs-trial-block sbs-trial-block-expired sbs-daily-trial-expired">
            <div class="sbs-trial-hd">
              <div class="sbs-trial-hd-left">
                <div class="sbs-trial-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                </div>
                <div>
                  <span class="sbs-trial-badge sbs-trial-badge-expired">Trial Ended</span>
                  <div class="sbs-trial-title">Your free trial has ended</div>
                </div>
              </div>
              <div class="sbs-trial-days">
                <div class="sbs-trial-days-num">0</div>
                <div class="sbs-trial-days-lbl">Days remaining</div>
              </div>
            </div>
            <div class="sbs-trial-progress-wrap">
              <div class="sbs-trial-progress-meta">
                <span>Trial completed</span>
                <span>Ended {{ nextBillingDateDisplay }}</span>
              </div>
              <div class="sbs-trial-progress">
                <div class="sbs-trial-progress-fill" style="width: 100%;"></div>
              </div>
            </div>
            <div class="sbs-trial-footnote">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span>{{ canManageBilling ? 'Add a payment method below to resume daily usage billing and keep your alerts running. Your tracked ASINs and data are unchanged.' : 'Your trial has ended and no payment method is on file. Ask your account admin to add a payment method to resume billing.' }}</span>
            </div>
            <button v-if="canManageBilling" type="button" class="sbs-btn sbs-btn-upgrade sbs-past-due-cta" @click="scrollToPaymentMethod">Add payment method →</button>
            <p v-else class="sbs-member-admin-hint">Ask your account admin to add a payment method on file.</p>
          </div>

          <div class="sbs-your-plan-card sbs-your-plan-daily">
            <span class="sbs-your-plan-badge">Your plan</span>
            <div class="sbs-your-plan-head">
              <div class="sbs-your-plan-head-main">
                <h2 class="sbs-your-plan-title">{{ dailyPlanTitle }}</h2>
                <p class="sbs-your-plan-desc">{{ dailyPlanSubtitle }}</p>
              </div>
              <div class="sbs-your-plan-next">
                <span class="sbs-your-plan-next-label">Next billing</span>
                <span class="sbs-your-plan-next-date">{{ nextBillingDateDisplay }}</span>
              </div>
            </div>
            <div class="sbs-your-plan-stats">
              <div class="sbs-your-plan-stat">
                <span class="sbs-your-plan-stat-label">Last billed</span>
                <span class="sbs-your-plan-stat-value">${{ summaryLastBilledAmount }}</span>
                <span class="sbs-your-plan-stat-sub">{{ summaryLastBilledDetail }}</span>
              </div>
              <div class="sbs-your-plan-stat">
                <span class="sbs-your-plan-stat-label">Projected this month</span>
                <span class="sbs-your-plan-stat-value">{{ projectedMonthUsesEstimate ? '~' : '' }}${{ projectedMonthAmount }}</span>
                <span class="sbs-your-plan-stat-sub">{{ projectedMonthSubtext }}</span>
              </div>
            </div>
            <div class="sbs-asin-meter">
              <span class="sbs-asin-meter-label">ASINs currently tracked</span>
              <span class="sbs-asin-meter-value">{{ summaryAsinsDisplay }} ASINs</span>
            </div>
            <p class="sbs-your-plan-foot">{{ dailyFootnote }}</p>
          </div>
          <button v-if="canManageBilling" type="button" class="sbs-email-cta" @click="openPlanChangeModal">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a1.99 1.99 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
            Looking to change plans? Email us →
          </button>
        </template>

        <!-- Agency / Enterprise upsell -->
        <div class="sbs-enterprise-bar">
          <div class="sbs-enterprise-bar-text">
            <strong>Agency / Enterprise</strong>
            <span>For white-label, invoicing, SLAs or volume terms · custom pricing from ~$199/mo</span>
          </div>
          <a href="mailto:support@sentrykit.com" class="sbs-enterprise-bar-btn">Talk to us →</a>
        </div>

        <!-- Payment method (admin / account owner only) -->
        <div id="payment-method" v-if="canManageBilling && ($root.paymentStatus !== 0 || billingVariant === 'new' || isDailyUsageTrialExpiredNoPayment)" class="sbs-payment-method-section">
          <h2 class="sbs-section-heading">Payment Method</h2>
          <p class="sbs-payment-method-sub">Used for all SentryKit subscription charges</p>

          <div v-if="savedPaymentMethods.length" class="sbs-pm-list">
            <div
              v-for="(pm, idx) in savedPaymentMethods"
              :key="pm.id || idx"
              :class="['sbs-pm-display', { 'sbs-pm-ach': pm.kind === 'ach' }]"
            >
              <span
                class="sbs-pm-brand"
                :class="{ 'sbs-pm-bank': pm.kind === 'ach' }"
                aria-hidden="true"
              >{{ pm.brandShort }}</span>
              <div class="sbs-pm-display-main">
                <div class="sbs-pm-display-top">
                  <span class="sbs-pm-display-title">{{ pm.label }}</span>
                  <div class="sbs-pm-display-actions">
                    <span v-if="pm.isDefault" class="sbs-pm-default-badge">Default</span>
                    <button
                      v-if="!pm.isDefault && pm.id && savedPaymentMethods.length > 1"
                      type="button"
                      class="sbs-pm-set-default-link"
                      :disabled="setDefaultInProgress || detachInProgress"
                      @click="setPaymentMethodAsDefault(pm.id)"
                    >Set as default</button>
                    <button
                      v-if="pm.id"
                      type="button"
                      class="sbs-pm-remove-link"
                      :disabled="detachInProgress"
                      @click="removePaymentMethod(pm)"
                    >Remove</button>
                  </div>
                </div>
                <span class="sbs-pm-display-meta">{{ pm.metaLine }}</span>
              </div>
            </div>
          </div>

          <div v-else-if="displayCardInfo" class="sbs-pm-display">
            <span class="sbs-pm-brand" aria-hidden="true">{{ displayCardInfo.brandShort }}</span>
            <div class="sbs-pm-display-main">
              <div class="sbs-pm-display-top">
                <span class="sbs-pm-display-title">{{ displayCardInfo.label }}</span>
                <div class="sbs-pm-display-actions">
                  <span class="sbs-pm-default-badge">Default</span>
                </div>
              </div>
              <span class="sbs-pm-display-meta">{{ displayCardInfo.metaLine }}</span>
            </div>
          </div>

          <div v-else-if="paymentMethod && paymentMethod.method === 'ach'" class="sbs-pm-display sbs-pm-ach">
            <span class="sbs-pm-brand sbs-pm-bank">ACH</span>
            <div class="sbs-pm-display-main">
              <div class="sbs-pm-display-top">
                <span class="sbs-pm-display-title">Bank transfer (ACH)</span>
                <div class="sbs-pm-display-actions">
                  <span class="sbs-pm-default-badge">Default</span>
                </div>
              </div>
              <span class="sbs-pm-display-meta">Tap “View bank details” for account info.</span>
            </div>
          </div>

          <div v-else class="sbs-pm-empty">
            <p>No card or bank account on file yet.</p>
          </div>

          <div class="sbs-pm-actions">
            <button type="button" class="sbs-btn sbs-btn-primary sbs-btn-with-icon" :disabled="updatePaymentInProgress" @click="openPrimaryPaymentUpdate">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-2.207 2.207L3 14v3h3l8.379-8.379-2.828-2.828z"/></svg>
              {{ primaryPaymentButtonLabel }}
            </button>
            <div id="sbsPaymentMethodsAdd" class="sbs-btn sbs-btn-outline sbs-btn-with-icon sbs-pm-add-wrap">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/></svg>
              Add new method
            </div>
            <div id="sbsPayPopupAdd" class="ui fluid popup sbs-pay-popup">
              <div class="ui two column center aligned very relaxed grid">
                <div class="column">
                  <h4 class="ui header">Card</h4>
                  <button type="button" class="ui labeled icon button" :class="{ loading: stripeButtonClicked || updatePaymentInProgress, disabled: updatePaymentInProgress }" @click="openStripeWidgetFromAdd">
                    <i class="credit card alternative icon"></i>Add card
                  </button>
                  <div class="ui top right attached teal label">Recommended</div>
                </div>
                <div class="column">
                  <h4 class="ui header">ACH transfer</h4>
                  <div class="ui labeled icon button" :class="{ loading: updatePaymentInProgress, disabled: updatePaymentInProgress || achAlreadyOnFile }" @click="createAchFromAdd">
                    <i class="university icon"></i>Set ACH
                  </div>
                  <div class="ui top right attached label">Beta</div>
                </div>
              </div>
              <div class="ui vertical divider">OR</div>
            </div>
          </div>

          <p class="sbs-billing-email-row">
            Billing email: <strong>{{ billingEmailDisplay }}</strong>
            <button type="button" class="sbs-link-inline" @click="openBillingEmailModal">Change</button>
          </p>

          <p v-if="billingVariant === 'new' && showNextBillLine" class="sbs-next-bill">Based on the current number of ASINs you are tracking, your next bill, charged to the payment method above, will be: <strong>${{ nextBillAmount }}</strong></p>

        </div>

        <div v-if="showResumeSubscriptionBanner" class="sbs-resume-banner" role="region" aria-label="Resume subscription">
          <div class="sbs-resume-banner-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 5v14l11-7L8 5z" fill="currentColor"/>
            </svg>
          </div>
          <div class="sbs-resume-banner-text">
            <h3 class="sbs-resume-banner-title">Subscription ending soon</h3>
            <p class="sbs-resume-banner-desc">{{ resumeBannerDescription }}</p>
          </div>
          <button
            type="button"
            class="sbs-resume-banner-btn"
            :disabled="resumeInProgress"
            @click="resumeSubscription"
          >{{ resumeInProgress ? 'Resuming…' : 'Resume subscription' }}</button>
        </div>

        <div v-else-if="showCancelSubscriptionBanner" class="sbs-cancel-banner" role="region" aria-label="Cancel subscription">
          <div class="sbs-cancel-banner-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><path d="M12 7v6M12 16h.01" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="sbs-cancel-banner-text">
            <h3 class="sbs-cancel-banner-title">Cancel subscription</h3>
            <p class="sbs-cancel-banner-desc">{{ cancelBannerDescription }}</p>
          </div>
          <button type="button" class="sbs-cancel-banner-btn" @click="openCancelWizard">Cancel subscription</button>
        </div>

        <div v-if="message" class="sbs-flash sbs-flash-ok" role="status">
          <span>{{ message }}</span>
          <button type="button" class="sbs-flash-close" aria-label="Dismiss" @click="message = ''">×</button>
        </div>
        <div v-if="error" class="sbs-flash sbs-flash-err" role="alert">
          <span>{{ error }}</span>
          <button type="button" class="sbs-flash-close" aria-label="Dismiss" @click="error = ''">×</button>
        </div>
      </div>

      <!-- Usage (Starter / Pro only) -->
      <div v-if="showUsageTab" v-show="activeTab === 'usage'" class="sbs-panel">
        <div v-if="usageTabLoading" class="sbs-usage-loading">
          <div class="sbs-spinner"></div>
        </div>
        <div v-else-if="usageTabError" class="sbs-usage-error">
          <p class="sbs-error-msg">{{ usageTabError }}</p>
          <button type="button" class="sbs-btn sbs-btn-outline" @click="loadUsageTabData">Retry</button>
        </div>
        <template v-else>
          <h2 class="sbs-usage-title">Usage</h2>
          <p class="sbs-usage-subtitle">{{ usageBillingPeriodLine }}</p>

          <div class="sbs-ucards">
            <div class="sbs-ucard">
              <div class="sbs-uc-lbl">Current plan</div>
              <div class="sbs-uc-val">{{ usageCurrentPlanName }}</div>
              <div class="sbs-uc-sub">{{ usagePlanPriceSubline }}</div>
            </div>
            <div class="sbs-ucard sbs-ucard-lever">
              <div class="sbs-uc-lbl">Storefronts connected</div>
              <div class="sbs-uc-val">{{ usageStorefrontsCountDisplay }}</div>
              <div class="sbs-uc-sub">The only thing that affects your price</div>
            </div>
            <div class="sbs-ucard">
              <div class="sbs-uc-lbl">Next charge</div>
              <div class="sbs-uc-val">{{ usageNextChargeShortDisplay }}</div>
              <div class="sbs-uc-sub">{{ usageNextChargeSubline }}</div>
            </div>
          </div>

          <div class="sbs-ublock">
            <h3 class="sbs-ublock-title">What we're monitoring for you</h3>
            <div class="sbs-ucards sbs-ucards-inner">
              <div class="sbs-ucard sbs-ucard-muted">
                <div class="sbs-uc-lbl">ASINs monitored</div>
                <div class="sbs-uc-val">{{ usageAsinsMonitoredDisplay }}</div>
                <div class="sbs-uc-sub">Unlimited on your plan</div>
              </div>
              <div class="sbs-ucard sbs-ucard-muted">
                <div class="sbs-uc-lbl">Marketplaces covered</div>
                <div class="sbs-uc-val">{{ usageMarketplacesCountDisplay }}</div>
                <div class="sbs-uc-sub">Unlimited on your plan</div>
              </div>
            </div>
          </div>

          <div class="sbs-ublock">
            <h3 class="sbs-ublock-title">What we caught this period</h3>
            <div v-if="!usageCategoriesWithBars.length" class="sbs-usage-empty">No changes recorded this period.</div>
            <div v-for="row in usageCategoriesWithBars" :key="row.key || row.label" class="sbs-usage-bar">
              <span class="sbs-usage-bar-name">{{ row.label }}</span>
              <span class="sbs-usage-bar-track"><span class="sbs-usage-bar-fill" :style="{ width: row.barWidth }"></span></span>
              <span class="sbs-usage-bar-num">{{ row.countDisplay }}</span>
            </div>
          </div>

        </template>
      </div>

      <!-- Billing History -->
      <div v-show="activeTab === 'history'" class="sbs-panel">
        <div class="sbs-history-card">
          <div class="sbs-history-head">
            <h2 class="sbs-history-title">Billing History</h2>
            <button v-if="invoices.length" type="button" class="sbs-link-download" @click="downloadAllInvoices">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
              Download all
            </button>
          </div>
          <div v-if="!invoices.length" class="sbs-empty-invoices">You have no invoices yet.</div>
          <div v-else class="sbs-history-table-wrap">
            <table class="sbs-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th class="sbs-col-amt">Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(inv, idx) in invoices" :key="idx">
                  <td>{{ formatInvoiceDate(invoicePaidOn(inv)) }}</td>
                  <td>{{ invoiceDescription(inv, idx) }}</td>
                  <td class="sbs-col-amt"><strong>${{ (inv.totalAmount / 100).toFixed(2) }}</strong></td>
                  <td><span class="sbs-status-paid"><span class="sbs-status-dot"></span>Paid</span></td>
                  <td><a v-if="inv.url" :href="inv.url" target="_blank" rel="noopener noreferrer" class="sbs-receipt-link">Receipt</a></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel wizard (Stripe → survey → dual mailers; legacy discount modals removed) -->
    <div v-if="cancelModalVisible" class="sbs-cancel-overlay" @click.self="closeCancelWizardIfIdle">
      <div class="sbs-cancel-sheet" role="dialog" aria-modal="true" aria-labelledby="sbs-cancel-title">
        <button type="button" class="sbs-cancel-close" :disabled="cancelFlowSubmitting" aria-label="Close" @click="closeCancelWizardIfIdle">&times;</button>
        <div class="sbs-cancel-progress" aria-hidden="true">
          <span v-for="n in 4" :key="n" :class="['sbs-cancel-progress-seg', cancelProgressSegClass(n)]"></span>
        </div>

        <header class="sbs-cancel-header">
          <h2 id="sbs-cancel-title" class="sbs-cancel-heading">Are you sure you want to cancel?</h2>
          <p class="sbs-cancel-subtitle">
            Your subscription will remain active until <strong>{{ cancelModalPeriodEndDisplay }}</strong>.
            You can resume anytime before then.
          </p>
        </header>

        <p v-if="cancelPreviewError && cancelModalStep === 1" class="sbs-cancel-alert">{{ cancelPreviewError }}</p>

        <div v-if="cancelModalStep === 1" class="sbs-cancel-body">
          <p v-if="cancelPreview && cancelPreview.alreadyScheduledForCancel" class="sbs-cancel-impact-plain">
            Cancellation is scheduled for the end of <strong>{{ cancelPreview.periodEndFormatted }}</strong>.
            You still have access until then. Click <strong>Resume subscription</strong> below to keep your plan active and billing on schedule.
          </p>
          <p v-else-if="cancelPreviewLoading" class="sbs-cancel-impact-plain">Loading your billing timeline…</p>
          <div v-else-if="cancelPreview" class="sbs-cancel-impact-box">
            <p class="sbs-cancel-impact-row sbs-cancel-impact-row--ok">
              <span class="sbs-cancel-impact-icon sbs-cancel-impact-icon--ok" aria-hidden="true">✓</span>
              <span>You'll keep full access until <strong>{{ cancelPreview.periodEndFormatted }}</strong>. No prorated refund — you've already paid for this period.</span>
            </p>
            <p class="sbs-cancel-impact-row">
              <span class="sbs-cancel-impact-icon sbs-cancel-impact-icon--warn" aria-hidden="true">⏱</span>
              <span>After that date, all <strong>real-time alerts</strong> stop. Buy Box monitoring, hijacker detection, suppression alerts — all paused.</span>
            </p>
            <p class="sbs-cancel-impact-row">
              <span class="sbs-cancel-impact-icon sbs-cancel-impact-icon--warn" aria-hidden="true">⏱</span>
              <span>Your <strong>30-day alert history, COGS data, and competitor profiles</strong> stay in your account for {{ cancelPreview.dataRetentionDays || 90 }} days in case you come back.</span>
            </p>
            <p v-if="cancelPreview.showLegacyMigrationWarning" class="sbs-cancel-warn-legacy sbs-cancel-warn-legacy--in-box">
              You migrated from our legacy billing platform — double-check exporting anything you rely on before access changes.
            </p>
            <p v-if="cancelPreview.stripeWillCancelImmediately" class="sbs-cancel-warn sbs-cancel-warn--in-box">
              We could not detect a reusable card or bank mandate on file, so cancelling will stop your subscription immediately.
            </p>
          </div>
        </div>
        <template v-else-if="cancelModalStep === 2">
          <div class="sbs-cancel-body">
            <div class="sbs-cancel-save-offer">
              <h3 class="sbs-cancel-save-offer-title">Wait — would a customized plan keep you here?</h3>
              <p class="sbs-cancel-save-offer-text">
                If pricing, capacity, or alert volume is the issue, we can build a plan around your specific setup — different ASIN cap, alert filtering, or a discounted rate. No pressure: it's just a 5-min email.
              </p>
              <button type="button" class="sbs-cancel-save-offer-yes" :disabled="cancelFlowSubmitting" @click="onCancelSaveOfferYes">
                Yes, talk to me about a custom plan
              </button>
              <button type="button" class="sbs-cancel-save-offer-no" :disabled="cancelFlowSubmitting" @click="onCancelSaveOfferNo">
                No thanks, continue cancelling
              </button>
              <p v-if="cancelFlowError" class="sbs-cancel-alert">{{ cancelFlowError }}</p>
            </div>
          </div>
        </template>
        <template v-else-if="cancelModalStep === 3">
          <div class="sbs-cancel-body sbs-cancel-body--survey">
            <p id="sbs-cancel-reason-heading" class="sbs-cancel-field-label">Why are you cancelling? <span class="sbs-cancel-req">*</span></p>
            <div class="sbs-cancel-reason-list" role="radiogroup" aria-labelledby="sbs-cancel-reason-heading">
              <label
                v-for="opt in cancelReasonOptions"
                :key="opt.id"
                :class="['sbs-cancel-reason-card', { 'sbs-cancel-reason-card--selected': cancelSurveyReason === opt.id }]"
              >
                <input v-model="cancelSurveyReason" class="sbs-cancel-reason-input" type="radio" :value="opt.id" />
                <span class="sbs-cancel-reason-copy">
                  <span class="sbs-cancel-reason-title">{{ opt.label }}</span>
                  <span class="sbs-cancel-reason-sub">{{ opt.subtitle }}</span>
                </span>
              </label>
            </div>
            <div v-if="cancelSurveyReason === 'too_many_alerts'" id="condAlerts" class="sbs-cancel-pills-block">
              <p class="sbs-cancel-pills-heading">Which alert types? (select all that apply)</p>
              <div class="sbs-cancel-pills-wrap">
                <label v-for="tag in cancelAlertTagOptions" :key="tag.id" :class="['sbs-cancel-pill', { 'sbs-cancel-pill--on': cancelSurveyAlertTags.indexOf(tag.id) !== -1 }]">
                  <input v-model="cancelSurveyAlertTags" type="checkbox" :value="tag.id" class="sbs-cancel-pill-input" />
                  {{ tag.label }}
                </label>
              </div>
              <p class="sbs-cancel-pills-hint">Helps us tune alert relevance for everyone.</p>
            </div>
            <div v-if="cancelSurveyReason === 'found_alternative'" id="condCompetitor" class="sbs-cancel-survey-section">
              <label class="sbs-cancel-field-label" for="sbs-cancel-comp">Which tool are you switching to?</label>
              <select id="sbs-cancel-comp" v-model="cancelSurveyCompetitor" class="sbs-cancel-select">
                <option value="">Select…</option>
                <option v-for="c in competitorOptions" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div class="sbs-cancel-survey-section">
              <label class="sbs-cancel-field-label">Is there anything we can do to make you stay?</label>
              <div class="sbs-cancel-segment" role="group" aria-label="Would you stay">
                <button
                  type="button"
                  :class="['sbs-cancel-segment-btn', { 'sbs-cancel-segment-btn--active': cancelStayAnswer === 'yes' }]"
                  @click="cancelStayAnswer = 'yes'"
                >Yes</button>
                <button
                  type="button"
                  :class="['sbs-cancel-segment-btn', { 'sbs-cancel-segment-btn--active': cancelStayAnswer === 'no' }]"
                  @click="cancelStayAnswer = 'no'"
                >No</button>
              </div>
            </div>
            <div class="sbs-cancel-survey-section">
              <label class="sbs-cancel-field-label" for="sbs-cancel-detail">
                Anything else you want us to know? <span class="sbs-cancel-opt">(optional)</span>
              </label>
              <textarea
                id="sbs-cancel-detail"
                v-model="cancelDetailText"
                rows="4"
                maxlength="7500"
                class="sbs-cancel-textarea"
                placeholder="What could we have done better? What feature would have changed your mind?"
              ></textarea>
            </div>
          </div>
        </template>
        <template v-else-if="cancelModalStep === 4">
          <div class="sbs-cancel-body">
            <div class="sbs-cancel-final-box">
              <p class="sbs-cancel-final-box-text">
                <span class="sbs-cancel-final-icon" aria-hidden="true">!</span>
                <strong>Final step.</strong>
                When you click "Confirm cancellation" below, your subscription will be set to cancel on
                <strong>{{ cancelModalPeriodEndDisplay }}</strong>. You can resume anytime before that date with one click.
              </p>
            </div>
            <p class="sbs-cancel-final-copy">
              After this, you'll get a confirmation email from us and your access will continue exactly as today until the end of the period. We'll be sad to see you go.
            </p>
            <p v-if="cancelFlowError" class="sbs-cancel-alert">{{ cancelFlowError }}</p>
          </div>
        </template>

        <footer class="sbs-cancel-footer">
          <button
            type="button"
            class="sbs-cancel-footer-keep"
            :disabled="cancelFlowSubmitting || resumeInProgress"
            @click="onCancelFooterKeep"
          >{{ cancelFooterKeepLabel }}</button>
          <button
            type="button"
            class="sbs-cancel-footer-continue"
            :disabled="cancelFooterContinueDisabled"
            @click="onCancelFooterContinue"
          >{{ cancelFooterContinueLabel }}</button>
        </footer>
      </div>
    </div>

    <div id="sbsPaymentElementModal" class="ui small modal">
      <i class="close icon" @click="unmountPaymentElement"></i>
      <div class="header">{{ paymentModalTitle }}</div>
      <div class="content">
        <div v-if="error" class="ui error message">{{ error }}</div>
        <div id="sbs-payment-element"></div>
        <div id="sbs-payment-element-errors" role="alert"></div>
      </div>
      <div class="actions">
        <div class="ui black deny button">Cancel</div>
        <div class="ui positive right labeled icon button" :class="{ loading: updatePaymentInProgress, disabled: updatePaymentInProgress }" @click="confirmPaymentMethod">
          Save<i class="checkmark icon"></i>
        </div>
      </div>
    </div>

    <div id="sbsPlanChangeModal" class="ui small modal">
      <div class="header">Plan changes</div>
      <div class="content">
            <p class="sbs-modal-copy">To change your subscription or discuss billing options, email our team.</p>
            <p><a class="ui primary button" :href="planChangeMailHref">Email support@sentrykit.com →</a></p>
      </div>
      <div class="actions">
        <div class="ui approve button">Close</div>
      </div>
    </div>

    <div id="sbsBillingEmailModal" class="ui small modal">
      <div class="header">Billing email</div>
      <div class="content">
        <p class="sbs-modal-copy">Receipts and billing notices are sent to <strong>{{ billingEmailDisplay }}</strong>.</p>
        <p class="sbs-modal-copy">To update the email on file with Stripe, contact support and we'll take care of it.</p>
        <p><a class="ui primary button" href="mailto:support@sentrykit.com?subject=SentryKit%20billing%20email%20change">Contact support →</a></p>
      </div>
      <div class="actions">
        <div class="ui approve button">Close</div>
      </div>
    </div>

    <div id="sbsAchDetailsModal" class="ui tiny modal">
      <i class="close icon"></i>
      <template v-if="paymentMethod">
        <div class="header">ACH details</div>
        <div v-if="paymentMethod.details" class="content" style="padding-bottom: 2em">
          <div class="ui relaxed divided list">
            <div v-for="(value, name) in paymentMethod.details" :key="name" class="item">
              <div class="header" style="text-transform: capitalize">{{ String(name).replace(/_/g, ' ') }}</div>
              <div class="description">{{ name === 'balance' ? (value / 100).toFixed(2) + ' USD' : value }}</div>
            </div>
          </div>
        </div>
      </template>
      <div class="actions">
        <div class="ui approve button">Close</div>
      </div>
    </div>

    <!-- Plan switch (Starter / Pro / Pro Annual): confirm or payment-required -->
    <div
      v-if="upgradeModalOpen"
      class="sbs-min-modal-backdrop"
      @click.self="closeUpgradeModal"
    >
      <div
        class="sbs-min-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sbs-upg-title"
        tabindex="-1"
        @click.stop
      >
        <template v-if="upgradeModalPhase === 'no_payment'">
          <h3 id="sbs-upg-title" class="sbs-min-modal-title">Add a card to choose {{ upgradeModalPlanName }}</h3>
          <p class="sbs-min-modal-body">
            {{ upgradeModalPlanName }} is <strong>{{ upgradeModalPriceLabel }}</strong>.
            {{ upgradeModalNoPaymentTail }}
          </p>
          <div class="sbs-min-modal-actions">
            <button type="button" class="sbs-min-modal-btn sbs-min-modal-btn-secondary" @click="closeUpgradeModal">Not now</button>
            <button type="button" class="sbs-min-modal-btn sbs-min-modal-btn-primary" @click="onUpgradeModalAddPayment">Add card</button>
          </div>
        </template>
        <template v-else>
          <h3 id="sbs-upg-title" class="sbs-min-modal-title">{{ upgradeModalTitle }}</h3>
          <dl class="sbs-min-modal-summary">
            <div><dt>New plan</dt><dd>{{ upgradeModalPlanName }} · {{ upgradeModalPriceLabel }}</dd></div>
            <div><dt>Charged today</dt><dd>{{ upgradeModalChargeToday }}</dd></div>
            <div><dt>{{ upgradeModalRenewLabel }}</dt><dd>{{ upgradeModalRenewValue }}</dd></div>
          </dl>
          <p v-if="upgradeModalNote" class="sbs-min-modal-body sbs-min-modal-note">{{ upgradeModalNote }}</p>
          <div class="sbs-min-modal-actions">
            <button type="button" class="sbs-min-modal-btn sbs-min-modal-btn-secondary" @click="closeUpgradeModal" :disabled="upgradeInProgress">Cancel</button>
            <button type="button" class="sbs-min-modal-btn sbs-min-modal-btn-primary" @click="confirmUpgradeModal" :disabled="upgradeInProgress">
              {{ upgradeInProgress ? 'Switching…' : upgradeModalConfirmLabel }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import {
  ORDER_VOLUME_UI_ENABLED,
  isOrderVolumeBillingEligible,
  isTrialOrderVolumeUser,
  userHasNoMonthlyOrderCap,
  resolveMonthlyOrderVolumeForDisplay,
  shouldShowOrderVolumeLimit,
  shouldShowOrderVolumeUi,
  currentBillingMonthDateStrings,
  proMonthlyPriceForStorefrontCount,
  connectedStorefrontsCountFromPeriodChanges,
  STARTER_MONTHLY_PRICE,
  proStorefrontUnitRate,
  PRO_INCLUDED_STOREFRONTS,
  proMonthlyOrderLimitForTenant,
  tenantIdFromIdentityBody,
  tenantIdFromOrderVolumeBody,
  syncTenantIdFromOrderVolume
} from '../../utils/orderVolumeBilling.js'
import { resolveBillingScreenVariant } from '../../utils/billingScreenVariant.js'
import { stripePublishableKey } from '../../utils/stripePublishableKey.js'
import {
  formatSubscriptionEndDate,
  currentPlanKeyFromSummary,
  isProPlanFromSummary,
  isFlatYearlyPlanFromSummary,
  isSubscriptionStatusActive,
  isSubscriptionStatusPastDue,
  isSubscriptionCancelScheduledActive,
  isTrialActiveFromSummary,
  isTrialExpiredFromSummary,
  normalizeSubscriptionSummary,
  PRO_ANNUAL_UI_ENABLED,
  SUBSCRIPTION_RESUME_URL,
  planNameFromSummary,
  subscriptionEndEpochFromSummary
} from '../../utils/subscriptionBilling.js'
import { formatLegacyBillingPricingLine } from '../../utils/productAddonBilling.js'
import {
  parsePeriodChangesBody,
  periodChangesCategoryBarWidth
} from '../../utils/watchdogSummaryApi.js'

export default {
  name: 'SubscriptionBillingSettings',
  data: function () {
    return {
      loading: true,
      loadError: null,
      activeTab: 'plans',
      isOwner: false,
      tenantId: null,
      hasDailyPricing: false,
      periodEnd: '',
      periodEndEpoch: null,
      periodStart: null,
      packageDetails: null,
      addonCount: 0,
      proStorefrontCount: 5,
      showVolumePricing: false,
      paymentStatusMessage: '',
      paymentMethod: null,
      hasPaymentDetails: false,
      subscriptionCanceled: false,
      resumeInProgress: false,
      invoices: [],
      nextBillingFromInvoices: null,
      message: '',
      error: '',
      stripeButtonClicked: false,
      upgradeInProgress: false,
      updatePaymentInProgress: false,
      cancelModalVisible: false,
      cancelModalStep: 1,
      cancelPreview: null,
      cancelPreviewLoading: false,
      cancelPreviewError: '',
      cancelFlowError: '',
      cancelFlowSubmitting: false,
      cancelSaveOfferReached: false,
      cancelSaveOutcome: null,
      cancelSurveyReason: '',
      cancelSurveyAlertTags: [],
      cancelSurveyCompetitor: '',
      cancelStayAnswer: '',
      cancelDetailText: '',
      planChangeMailtoOverride: '',
      cancelReasonOptions: [
        { id: 'too_expensive', label: "It's too expensive", subtitle: "Pricing isn't right for me" },
        { id: 'missing_features', label: 'Missing features I need', subtitle: "The product doesn't do what I need" },
        {
          id: 'too_many_alerts',
          label: "Too many alerts and not the ones I'm looking for",
          subtitle: 'Alert volume or relevance is the issue'
        },
        { id: 'found_alternative', label: 'I found a better alternative', subtitle: 'Switching to another tool' },
        { id: 'not_selling_amazon', label: 'No longer selling on Amazon', subtitle: 'Closed my Amazon business or paused' },
        { id: 'something_else', label: 'Something else', subtitle: 'None of the above' }
      ],
      cancelAlertTagOptions: [
        { id: 'buy_box_lost', label: 'Buy Box Lost' },
        { id: 'hijacker_detected', label: 'Hijacker Detected' },
        { id: 'listing_suppressed', label: 'Listing Suppressed' },
        { id: 'out_of_stock', label: 'Out of Stock' },
        { id: 'competitor_price_change', label: 'Competitor Price Change' },
        { id: 'low_stock', label: 'Low Stock' },
        { id: 'price_floor_breach', label: 'Price Floor Breach' },
        { id: 'sales_volume_drop', label: 'Sales Volume Drop' },
        { id: 'content_changed', label: 'Content Changed' },
        { id: 'margin_threshold', label: 'Margin Threshold' },
        { id: 'daily_portfolio_digest', label: 'Daily Portfolio Digest' }
      ],
      competitorOptions: [
        'Prefer not to say',
        'BQool',
        'Aura',
        'Seller Snap',
        'RepricerExpress',
        'Informed.co',
        'Going manual / no tool',
        'Other'
      ],
      stripeInstance: null,
      paymentElement: null,
      elementsGroup: null,
      setupIntentClientSecret: null,
      calculatedTotalDailyCost: 0,
      forcastedAmount: 0,
      dailyCostBreakdownData: [],
      showForecastedColumn: true,
      billingSummary: {},
      billingUserTypeRawFromIdentity: null,
      paymentModalTitle: 'Update payment method',
      savedPaymentMethods: [],
      upgradeModalOpen: false,
      upgradeModalPhase: 'confirm',
      upgradeModalPlanKey: 'pro',
      setDefaultInProgress: false,
      detachInProgress: false,
      orderVolumeLoading: false,
      orderVolumeError: null,
      orderVolumeData: null,
      orderVolumeChecked: false,
      periodChangesLoading: false,
      periodChangesError: null,
      periodChangesData: null,
      periodChangesFetched: false,
      /** GET /api/billing/subscription/summary */
      subscriptionSummary: null,
      selectPlanInProgress: false
    }
  },
  watch: {
    activeTab: function (tab) {
      if (tab === 'usage' && this.showUsageTab && (!this.orderVolumeChecked || !this.periodChangesFetched) && !this.usageTabLoading) {
        this.loadUsageTabData()
      }
    },
    showUsageTab: function (visible) {
      if (!visible && this.activeTab === 'usage') {
        this.activeTab = 'plans'
      }
    },
    '$route.query.tab': function () {
      this.applyBillingTabFromRoute()
    },
    '$route.query.upgrade': function () {
      this.tryFulfillUpgradeRouteIntent()
    },
    '$route.query.plan': function () {
      this.tryApplyPlanQuerySelection()
    }
  },
  computed: {
    billingVariant: function () {
      var raw =
        (this.billingSummary && this.billingSummary.userType) ||
        (this.billingSummary && this.billingSummary.user_type) ||
        this.billingUserTypeRawFromIdentity ||
        (this.packageDetails && this.packageDetails.subscriptionBillingUserType)
      return this.normalizeBillingScreenVariant(raw)
    },
    billingEmailDisplay: function () {
      var fromSummary = this.billingSummary && (this.billingSummary.billingEmail || this.billingSummary.billing_email)
      if (fromSummary) return fromSummary
      return this.$root.userEmail || '—'
    },
    planChangeMailHref: function () {
      if (this.planChangeMailtoOverride) return this.planChangeMailtoOverride
      return 'mailto:support@sentrykit.com?subject=SentryKit%20plan%20change'
    },
    isCancelScheduledActive: function () {
      return isSubscriptionCancelScheduledActive(this.subscriptionSummary)
    },
    showResumeSubscriptionBanner: function () {
      return !!(this.canManageBilling && this.isCancelScheduledActive)
    },
    resumeBannerDescription: function () {
      var end = this.nextBillingDateDisplay
      if (end && end !== '—') {
        return 'Your plan is set to cancel on ' + end + '. Resume now to stay on ' + this.currentPlanTitle + ' with no interruption.'
      }
      return 'Your plan is set to cancel at the end of this billing period. Resume now to keep it active.'
    },
    showCancelSubscriptionBanner: function () {
      if (this.isCancelScheduledActive) return false
      var ps = this.$root.paymentStatus
      var payStates = ps === 2 || ps === 3 || ps === 4
      return !!(this.isOwner && !this.subscriptionCanceled && payStates)
    },
    cancelFooterKeepLabel: function () {
      if (this.cancelModalStep === 1 && this.cancelPreview && this.cancelPreview.alreadyScheduledForCancel) {
        return this.resumeInProgress ? 'Resuming…' : 'Resume subscription'
      }
      return 'Keep my subscription'
    },
    cancelBannerDescription: function () {
      var p = this.cancelPreview
      if (p && p.periodEndFormatted) {
        return "You'll keep full access until " + p.periodEndFormatted + '. No proration.'
      }
      return "You'll keep full access until the end of your current billing period. No proration."
    },
    cancelReasonSummaryLabel: function () {
      var self = this
      var hit = this.cancelReasonOptions.find(function (o) {
        return o.id === self.cancelSurveyReason
      })
      return hit ? hit.label : this.cancelSurveyReason || '—'
    },
    cancelSurveyAlertHuman: function () {
      var map = {}
      this.cancelAlertTagOptions.forEach(function (t) {
        map[t.id] = t.label
      })
      return this.cancelSurveyAlertTags
        .map(function (id) {
          return map[id] || id
        })
        .join(', ')
    },
    cancelModalPeriodEndDisplay: function () {
      var p = this.cancelPreview
      if (p && p.periodEndFormatted) return p.periodEndFormatted
      if (this.periodEnd && this.periodEnd !== '—') return this.periodEnd
      return 'the end of your billing period'
    },
    cancelFooterContinueLabel: function () {
      if (this.cancelModalStep === 4) {
        return this.cancelFlowSubmitting ? 'Submitting…' : 'Confirm cancellation'
      }
      return 'Continue →'
    },
    cancelFooterContinueDisabled: function () {
      if (this.cancelFlowSubmitting) return true
      if (this.cancelModalStep === 1) {
        if (this.cancelPreviewLoading) return true
        if (!this.cancelPreview) return true
        if (this.cancelPreview.alreadyScheduledForCancel) return true
        return false
      }
      if (this.cancelModalStep === 3) return !this.cancelSurveyReason
      return false
    },
    legacyPlanTitle: function () {
      if (this.billingSummary && this.billingSummary.planName) return this.billingSummary.planName
      if (this.billingSummary && this.billingSummary.plan_title) return this.billingSummary.plan_title
      var n = this.packageDetails && (this.packageDetails.name || this.packageDetails.packageName)
      return n || 'SentryKit Legacy'
    },
    legacyPricingLine: function () {
      if (this.billingSummary && this.billingSummary.pricingDescription) return this.billingSummary.pricingDescription
      if (this.billingSummary && this.billingSummary.pricing_description) return this.billingSummary.pricing_description
      return formatLegacyBillingPricingLine(this.packageDetails, this.addonCount)
    },
    legacyFootnote: function () {
      return (
        (this.billingSummary && (this.billingSummary.grandfatherNote || this.billingSummary.grandfather_note)) ||
        "You're on a grandfathered plan. Pricing and features have been preserved from your original signup."
      )
    },
    dailyPlanTitle: function () {
      if (this.hasDailyPricing) return 'SentryKit Daily Usage Plan'
      return (
        (this.billingSummary && (this.billingSummary.planName || this.billingSummary.plan_title)) ||
        'SentryKit Daily Usage Plan'
      )
    },
    dailyPlanSubtitle: function () {
      return (
        (this.billingSummary && (this.billingSummary.planSubtitle || this.billingSummary.plan_subtitle)) ||
        'Billed monthly based on the number of ASINs you track'
      )
    },
    dailyFootnote: function () {
      return (
        (this.billingSummary && (this.billingSummary.dailyDisclaimer || this.billingSummary.daily_disclaimer)) ||
        'Your invoice amount changes when ASIN counts change. Adjust tracked ASINs anytime from the Products page.'
      )
    },
    summaryLastBilledAmount: function () {
      var cents =
        this.billingSummary &&
        (this.billingSummary.lastBilledAmountCents != null
          ? this.billingSummary.lastBilledAmountCents
          : this.billingSummary.last_billed_amount_cents)
      if (cents != null) return (Number(cents) / 100).toFixed(2)
      var inv = this.invoices[0]
      if (inv && inv.totalAmount != null) return (inv.totalAmount / 100).toFixed(2)
      return '—'
    },
    summaryLastBilledDetail: function () {
      var dateRaw =
        this.billingSummary &&
        (this.billingSummary.lastBilledDate ||
          this.billingSummary.last_billed_date ||
          this.billingSummary.lastBilledAt)
      var label =
        (this.billingSummary && (this.billingSummary.lastBilledStatus || this.billingSummary.last_billed_status)) ||
        'Paid'
      if (dateRaw) return this.formatInvoiceDate(dateRaw) + ' · ' + label
      var inv = this.invoices[0]
      if (inv) return this.formatInvoiceDate(this.invoicePaidOn(inv)) + ' · ' + label
      return '—'
    },
    summaryAsinsDisplay: function () {
      var bs = this.billingSummary
      var n =
        bs &&
        (bs.trackedAsinsCount != null
          ? bs.trackedAsinsCount
          : bs.tracked_asins_count != null
            ? bs.tracked_asins_count
            : bs.asinsTracked != null
              ? bs.asinsTracked
              : bs.asins_tracked)
      if (n != null && n !== '') return String(n)
      var rows = this.dailyCostBreakdownData
      if (rows && rows.length) {
        var last = rows[rows.length - 1]
        if (last && last.quantity != null) return String(last.quantity)
      }
      return '—'
    },
    projectedMonthAmount: function () {
      var cents =
        this.billingSummary &&
        (this.billingSummary.projectedMonthAmountCents != null
          ? this.billingSummary.projectedMonthAmountCents
          : this.billingSummary.projected_month_amount_cents)
      if (cents != null) return (Number(cents) / 100).toFixed(2)
      if (this.billingVariant === 'daily_usage' || this.hasDailyPricing) {
        if (this.dailyCostBreakdownData.length) {
          return (this.calculatedTotalDailyCost + this.forcastedAmount).toFixed(2)
        }
        return '0.00'
      }
      return this.nextBillAmount
    },
    projectedMonthUsesEstimate: function () {
      if (this.billingVariant !== 'daily_usage' && !this.hasDailyPricing) return false
      return this.dailyCostBreakdownData.length > 0
    },
    projectedMonthSubtext: function () {
      if (this.isDailyUsageTrialExpiredNoPayment) {
        return 'Add a payment method to resume billing'
      }
      if ((this.billingVariant === 'daily_usage' || this.hasDailyPricing) && !this.dailyCostBreakdownData.length) {
        return 'No usage billed this period yet'
      }
      return 'Based on current ASIN count'
    },
    displayCardInfo: function () {
      if (!this.paymentMethod || this.paymentMethod.method !== 'card') return null
      var pm = this.paymentMethod
      var d = pm.details || pm.card || {}
      var brand = (pm.brand || d.brand || d.cardBrand || 'Card').toString()
      var short = brand.length <= 4 ? brand.toUpperCase() : brand.slice(0, 4).toUpperCase()
      var last4 = (pm.last4 != null ? pm.last4 : d.last4 || d.lastFour || '••••').toString()
      var expM = pm.exp_month != null ? pm.exp_month : pm.expMonth != null ? pm.expMonth : d.exp_month != null ? d.exp_month : d.expMonth
      var expY = pm.exp_year != null ? pm.exp_year : pm.expYear != null ? pm.expYear : d.exp_year != null ? d.exp_year : d.expYear
      var prettyBrand = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
      var metaParts = ['Brand: ' + prettyBrand, 'Last 4: ' + last4]
      var expDisp = this.formatCardExpiryDisplay(expM, expY)
      if (expDisp) metaParts.push('Expires: ' + expDisp)
      var created = d.created || pm.createdAt || pm.created
      if (created) {
        var cd = typeof created === 'number' ? new Date(created * 1000) : new Date(created)
        if (!isNaN(cd.getTime())) {
          metaParts.push(
            'Added ' +
              cd.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          )
        }
      }
      var label = prettyBrand + ' •••• ' + last4
      return { brandShort: short, label: label, metaLine: metaParts.join(' · ') || 'Card on file' }
    },
    primaryPaymentButtonLabel: function () {
      if (this.paymentMethod && this.paymentMethod.method === 'ach') return 'View bank details'
      var listHasCard = this.savedPaymentMethods.some(function (p) {
        return p.kind === 'card'
      })
      if (
        listHasCard ||
        (this.hasPaymentDetails && this.paymentMethod && this.paymentMethod.method === 'card')
      ) {
        return 'Update card'
      }
      return 'Add card'
    },
    showNextBillLine: function () {
      return this.hasDailyPricing || this.billingVariant === 'daily_usage'
    },
    isNewBillingVariant: function () {
      return this.billingVariant === 'new'
    },
    isPaymentPastDue: function () {
      if (!this.isNewBillingVariant) return false
      return isSubscriptionStatusPastDue(this.subscriptionSummary)
    },
    isTrialBillingActive: function () {
      if (!this.isNewBillingVariant) return false
      if (this.isPaymentPastDue) return false
      if (this.subscriptionSummary) return isTrialActiveFromSummary(this.subscriptionSummary)
      return isTrialOrderVolumeUser(this.orderVolumeContext)
    },
    /** True when summary has trialExpired and status is not active */
    isTrialBillingExpired: function () {
      if (!this.isNewBillingVariant) return false
      if (this.isPaymentPastDue) return false
      return isTrialExpiredFromSummary(this.subscriptionSummary)
    },
    /** Daily usage plan: trial ended and no card/bank on file (subscription summary + payment-methods). */
    isDailyUsageTrialExpiredNoPayment: function () {
      if (this.billingVariant !== 'daily_usage') return false
      if (!isTrialExpiredFromSummary(this.subscriptionSummary)) return false
      return !this.hasStripePaymentMethodOnFile
    },
    /** Payment method from Stripe APIs only (not legacy hasPaymentDetails default). */
    hasStripePaymentMethodOnFile: function () {
      if (this.subscriptionSummary && this.subscriptionSummary.paymentInfoAdded) return true
      if (this.savedPaymentMethods && this.savedPaymentMethods.length > 0) return true
      if (this.displayCardInfo) return true
      if (this.paymentMethod && this.paymentMethod.method === 'ach') return true
      if (this.paymentMethod && this.paymentMethod.method === 'card') return true
      return false
    },
    /** Account owner (admin) — can change payment, plan, and cancel. */
    canManageBilling: function () {
      return this.isOwner
    },
    hasTenantBillingSnapshot: function () {
      return !!(
        this.packageDetails ||
        this.subscriptionSummary ||
        (this.billingSummary && Object.keys(this.billingSummary).length > 0) ||
        this.invoices.length > 0
      )
    },
    billingPageSubtitle: function () {
      if (!this.canManageBilling) {
        return 'View your plan, usage, and billing history. Contact your account admin to make changes.'
      }
      if (this.isPaymentPastDue) return 'Your last payment failed. Update your payment method to keep your subscription active.'
      if (this.isTrialBillingExpired) return 'Your trial has ended. Pick a plan to resume service.'
      if (this.isDailyUsageTrialExpiredNoPayment) {
        return 'Your trial has ended. Add a payment method to resume daily usage billing.'
      }
      return 'Manage your plan, review usage, and access billing history.'
    },
    isFlatYearlyPlan: function () {
      if (isFlatYearlyPlanFromSummary(this.subscriptionSummary)) return true
      if (!this.packageDetails) return false
      var key = (this.packageDetails.packageKey || this.packageDetails.package_key || '').toString().toUpperCase()
      if (key === 'FLAT_YEARLY') return true
      var tier = (this.packageDetails.tier || '').toString().toLowerCase()
      if (tier === 'flat_yearly') return true
      var name = (this.packageDetails.name || this.packageDetails.packageName || this.packageDetails.planLabel || '').toLowerCase()
      return name.indexOf('flat yearly') >= 0
    },
    isProPlan: function () {
      if (this.isFlatYearlyPlan) return false
      if (this.subscriptionSummary && planNameFromSummary(this.subscriptionSummary)) {
        return isProPlanFromSummary(this.subscriptionSummary)
      }
      if (!this.packageDetails) return false
      var key = (this.packageDetails.packageKey || this.packageDetails.package_key || '').toString().toUpperCase()
      if (key === 'PRO' || key === 'PRO_ANNUAL') return true
      var name = (this.packageDetails.name || this.packageDetails.packageName || '').toLowerCase()
      if (name.indexOf('pro') >= 0) return true
      var cents = this.packageDetails.baseSubscriptionFeeCents
      return cents != null && cents >= 4900
    },
    currentPlanTitle: function () {
      if (this.isFlatYearlyPlan) return 'Flat Yearly Subscription'
      var fromSummary = planNameFromSummary(this.subscriptionSummary)
      if (fromSummary) return fromSummary
      return (this.isProPlan ? 'Pro' : 'Starter') + ' Plan'
    },
    flatYearlyPriceLabel: function () {
      var cents = this.packageDetails && (this.packageDetails.baseSubscriptionFeeCents || this.packageDetails.base_subscription_fee_cents)
      var dollars = cents != null && cents !== '' && !isNaN(Number(cents)) ? Number(cents) / 100 : 600
      var amount = Math.floor(dollars) === dollars ? String(dollars) : dollars.toFixed(2)
      return '$' + amount + ' / year'
    },
    currentPlanSubtitle: function () {
      if (this.isPaymentPastDue) {
        return 'Payment failed — update your card below to stay on this plan'
      }
      if (this.isFlatYearlyPlan) return this.flatYearlyPriceLabel
      if (this.isTrialBillingActive) {
        return 'Free trial · ' + this.planAfterTrialLabel + ' (' + this.planPriceLabel(this.planAfterTrialKey) + ') after trial'
      }
      if (this.isProAnnualPlan) return 'Pro Annual · ' + this.planPriceLabel('pro_annual') + ', billed yearly'
      if (this.isProPlan) return 'Pro · ' + this.planPriceLabel('pro')
      return 'Starter · ' + this.planPriceLabel('starter')
    },
    subscriptionStatusLabel: function () {
      if (!this.subscriptionSummary || !this.subscriptionSummary.status) return ''
      if (this.isPaymentPastDue) return 'Payment failed'
      if (this.isTrialBillingExpired) return 'Trial ended'
      if (this.isTrialBillingActive) return 'Trial'
      if (this.isCancelScheduledActive) return 'Cancels ' + this.nextBillingDateDisplay
      if (this.subscriptionSummary.status === 'active') return 'Active'
      return this.subscriptionSummary.status
    },
    nextBillingDateDisplay: function () {
      if (this.subscriptionSummary) {
        var fromSummary = formatSubscriptionEndDate(this.subscriptionSummary, function (d) {
          return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        })
        if (fromSummary !== '—') return fromSummary
      }
      var fromInv =
        this.nextBillingFromInvoices != null && String(this.nextBillingFromInvoices).trim()
          ? String(this.nextBillingFromInvoices).trim()
          : ''
      if (fromInv) return fromInv
      if (!this.periodEnd || this.periodEnd === '-') return '—'
      return this.periodEnd
    },
    trialPeriodEndEpoch: function () {
      if (this.subscriptionSummary) {
        var e = subscriptionEndEpochFromSummary(this.subscriptionSummary)
        if (e != null) return e
      }
      return this.periodEndEpoch
    },
    nextBillAmount: function () {
      if (this.hasDailyPricing && this.dailyCostBreakdownData.length) {
        return (this.calculatedTotalDailyCost + this.forcastedAmount).toFixed(2)
      }
      if (this.packageDetails) {
        var base = this.packageDetails.baseSubscriptionFeeCents || 0
        var add = (this.packageDetails.addonSubscriptionFeeCents || 0) * (this.addonCount || 0)
        return ((base + add) / 100).toFixed(2)
      }
      return '0.00'
    },
    achAlreadyOnFile: function () {
      if (this.paymentMethod && this.paymentMethod.method === 'ach') return true
      return this.savedPaymentMethods.some(function (p) {
        return p.kind === 'ach'
      })
    },
    /** True when Stripe/API indicate the tenant can be charged (card, saved PMs, or legacy source). */
    hasPaymentMethodOnFile: function () {
      if (this.subscriptionSummary && this.subscriptionSummary.paymentInfoAdded) return true
      if (this.savedPaymentMethods && this.savedPaymentMethods.length > 0) return true
      if (this.displayCardInfo) return true
      if (this.paymentMethod && this.paymentMethod.method === 'ach') return true
      if (this.paymentMethod && this.paymentMethod.method === 'card') return true
      return !!this.hasPaymentDetails
    },
    orderVolumeEligible: function () {
      return isOrderVolumeBillingEligible(this.orderVolumeData)
    },
    showUsageTab: function () {
      if (!ORDER_VOLUME_UI_ENABLED) return false
      return shouldShowOrderVolumeUi(this.packageDetails, this.orderVolumeData, {
        billingVariant: this.billingVariant,
        subscriptionSummary: this.subscriptionSummary,
        hasDailyPricing: this.hasDailyPricing,
        billingUserTypeRaw: this.billingUserTypeRawFromIdentity,
        billingSummary: this.billingSummary
      })
    },
    effectiveTenantId: function () {
      return (
        tenantIdFromOrderVolumeBody(this.orderVolumeData) ||
        this.tenantId ||
        (this.$root && this.$root.tenantId) ||
        null
      )
    },
    proMonthlyOrderCap: function () {
      return proMonthlyOrderLimitForTenant(this.effectiveTenantId)
    },
    proOrderCapLabel: function () {
      var cap = this.proMonthlyOrderCap
      return cap != null ? cap.toLocaleString('en-US') : ''
    },
    proStorefrontRate: function () {
      return proStorefrontUnitRate(this.proStorefrontCount + 1)
    },
    proStorefrontTotalPrice: function () {
      return proMonthlyPriceForStorefrontCount(this.proStorefrontCount)
    },
    proStorefrontHint: function () {
      var n = this.proStorefrontCount
      var INCL = PRO_INCLUDED_STOREFRONTS
      if (n < INCL) return (INCL - n) + ' more storefront' + ((INCL - n) > 1 ? 's' : '') + ' included'
      return INCL + ' included · next storefront +$' + this.proStorefrontRate
    },
    userHasNoMonthlyOrderCap: function () {
      return userHasNoMonthlyOrderCap(this.orderVolumeContext)
    },
    orderVolumeContext: function () {
      return {
        orderVolumeBody: this.orderVolumeData,
        packageDetails: this.packageDetails,
        paymentStatus: this.$root.paymentStatus,
        hasPaymentMethodOnFile: this.hasPaymentMethodOnFile,
        subscriptionSummary: this.subscriptionSummary,
        tenantId: this.effectiveTenantId
      }
    },
    isTrialOrderVolumeUser: function () {
      return isTrialOrderVolumeUser(this.orderVolumeContext)
    },
    orderVolumeMonthly: function () {
      return resolveMonthlyOrderVolumeForDisplay(
        this.packageDetails,
        this.orderVolumeData,
        this.periodEndEpoch,
        this.orderVolumeContext
      )
    },
    orderVolumeShowLimit: function () {
      return shouldShowOrderVolumeLimit(this.orderVolumeMonthly, this.orderVolumeContext)
    },
    orderVolumePeriodRange: function () {
      var dates = currentBillingMonthDateStrings(this.periodStart, this.periodEndEpoch)
      if (!dates) return '—'
      return this.formatUsageDateRange(dates.periodStart, dates.periodEnd)
    },
    orderVolumeUsedDisplay: function () {
      return this.formatUsageCount(this.orderVolumeMonthly && this.orderVolumeMonthly.used)
    },
    orderVolumeLimitDisplay: function () {
      return this.formatUsageCount(this.orderVolumeMonthly && this.orderVolumeMonthly.limit)
    },
    orderVolumePercentUsed: function () {
      var pct = this.orderVolumeMonthly && this.orderVolumeMonthly.percentUsed
      if (pct == null || isNaN(Number(pct))) return 0
      return Math.min(100, Math.max(0, Number(pct)))
    },
    orderVolumeProgressWidth: function () {
      return this.orderVolumePercentUsed + '%'
    },
    orderVolumePercentDisplay: function () {
      var m = this.orderVolumeMonthly
      if (!m || m.percentUsed == null || isNaN(Number(m.percentUsed))) return '0.0'
      return Number(m.percentUsed).toFixed(1)
    },
    orderVolumeRemainingDisplay: function () {
      return this.formatUsageCount(this.orderVolumeMonthly && this.orderVolumeMonthly.remaining)
    },
    orderVolumeResetsDisplay: function () {
      var raw = this.orderVolumeMonthly && this.orderVolumeMonthly.resetsAt
      return raw ? this.formatUsageShortDate(raw) : '—'
    },
    orderVolumeOrdersThisMonthDisplay: function () {
      return this.formatUsageCount(this.orderVolumeData && this.orderVolumeData.ordersThisMonth)
    },
    orderVolumeOrdersCompareLine: function () {
      var d = this.orderVolumeData
      if (!d) return '—'
      var last = this.formatUsageCount(d.ordersLastMonth)
      var pct = d.ordersChangePercent
      if (pct == null || isNaN(Number(pct))) return 'vs. ' + last + ' last month'
      var sign = Number(pct) > 0 ? '+' : ''
      return 'vs. ' + last + ' last month (' + sign + Number(pct).toFixed(1) + '%)'
    },
    orderVolumeAvgDailyDisplay: function () {
      var n = this.orderVolumeData && this.orderVolumeData.avgDailyOrders
      if (n == null || isNaN(Number(n))) return '—'
      return Number(n).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    },
    orderVolumeProjectedDisplay: function () {
      return this.formatUsageCount(this.orderVolumeData && this.orderVolumeData.projectedMonthEndOrders)
    },
    orderVolumeConnectedCountDisplay: function () {
      var accounts = (this.orderVolumeData && this.orderVolumeData.connectedAccounts) || []
      return String(accounts.length)
    },
    orderVolumeConnectedAccountsLine: function () {
      var accounts = (this.orderVolumeData && this.orderVolumeData.connectedAccounts) || []
      if (!accounts.length) return 'No connected seller accounts'
      if (accounts.length === 1) {
        var a = accounts[0]
        var name = (a.name || a.sellerName || 'Seller').trim()
        var mps = (a.marketplaces || []).join(', ')
        return mps ? name + ' · ' + mps : name
      }
      return accounts
        .map(function (a) {
          var n = (a.name || a.sellerName || 'Seller').trim()
          var mp = (a.marketplaces || []).join(', ')
          return mp ? n + ' (' + mp + ')' : n
        })
        .join(' · ')
    },
    orderVolumeAsinsDisplay: function () {
      return this.formatUsageCount(this.orderVolumeData && this.orderVolumeData.asinsMonitored)
    },
    orderVolumeChangesCaughtDisplay: function () {
      var pc = this.periodChangesParsed
      if (pc && pc.total != null && !isNaN(Number(pc.total))) {
        return Number(pc.total).toLocaleString('en-US')
      }
      if (pc && pc.watchdogChangesCount != null && !isNaN(Number(pc.watchdogChangesCount))) {
        return Number(pc.watchdogChangesCount).toLocaleString('en-US')
      }
      var n = this.orderVolumeData && this.orderVolumeData.changesCaught
      if (n == null || isNaN(Number(n))) return '—'
      return Number(n).toLocaleString('en-US')
    },
    periodChangesParsed: function () {
      return parsePeriodChangesBody(this.periodChangesData)
    },
    usageTabLoading: function () {
      return this.orderVolumeLoading || this.periodChangesLoading
    },
    usageTabError: function () {
      return this.orderVolumeError
    },
    usageBillingPeriodLine: function () {
      var pc = this.periodChangesParsed
      var start = pc.periodStart
      var end = pc.periodEnd
      if (start && end) {
        var range = this.formatUsageDateRange(start, end)
        var status = this.usagePeriodStatusSuffix
        return status ? 'Billing period: ' + range + ' · ' + status : 'Billing period: ' + range
      }
      var dates = currentBillingMonthDateStrings(this.periodStart, this.periodEndEpoch)
      if (!dates) return 'Billing period: —'
      var fallback = this.formatUsageDateRange(dates.periodStart, dates.periodEnd)
      var suffix = this.usagePeriodStatusSuffix
      return suffix ? 'Billing period: ' + fallback + ' · ' + suffix : 'Billing period: ' + fallback
    },
    usagePeriodStatusSuffix: function () {
      if (this.isTrialBillingExpired) return 'trial ended'
      if (this.isTrialBillingActive) return 'trial'
      if (this.subscriptionStatusLabel) return String(this.subscriptionStatusLabel).toLowerCase()
      return ''
    },
    usageCurrentPlanName: function () {
      if (this.isFlatYearlyPlan) return 'Flat Yearly Subscription'
      if (this.isInTrialState) return 'Free trial'
      return this.planDisplayName(this.currentPlanKey) || (this.isProPlan ? 'Pro' : 'Starter')
    },
    usagePlanPriceSubline: function () {
      if (this.isFlatYearlyPlan) return this.flatYearlyPriceLabel
      if (this.isInTrialState) return this.planAfterTrialLabel + ' after trial · ' + this.planPriceLabel(this.planAfterTrialKey)
      if (this.isProAnnualPlan) return this.planPriceLabel('pro_annual')
      if (this.isProPlan) {
        var count = connectedStorefrontsCountFromPeriodChanges(this.periodChangesData)
        if (count == null) count = this.proStorefrontCount
        var price = proMonthlyPriceForStorefrontCount(count)
        return '$' + price.toLocaleString('en-US') + (this.isTrialBillingActive ? ' / mo after trial' : ' / mo')
      }
      return this.isTrialBillingActive ? '$' + STARTER_MONTHLY_PRICE + ' / mo after trial' : '$' + STARTER_MONTHLY_PRICE + ' / mo'
    },
    usageStorefrontsCountDisplay: function () {
      var pc = this.periodChangesParsed
      if (pc.connectedStorefrontsCount != null && !isNaN(Number(pc.connectedStorefrontsCount))) {
        return this.formatUsageCount(pc.connectedStorefrontsCount)
      }
      return this.orderVolumeConnectedCountDisplay
    },
    usageNextChargeShortDisplay: function () {
      var epoch = this.trialPeriodEndEpoch
      if (epoch != null) {
        var d = new Date(Number(epoch) * 1000)
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        }
      }
      var full = this.nextBillingDateDisplay
      if (!full || full === '—') return '—'
      var parsed = new Date(full)
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
      return full
    },
    usageNextChargeSubline: function () {
      if (this.isTrialBillingActive) return 'When your trial ends'
      if (this.isTrialBillingExpired) return 'Trial ended'
      return 'Next billing date'
    },
    usageAsinsMonitoredDisplay: function () {
      var pc = this.periodChangesParsed
      if (pc.activeListedAsinsCount != null && !isNaN(Number(pc.activeListedAsinsCount))) {
        return this.formatUsageCount(pc.activeListedAsinsCount)
      }
      return this.orderVolumeAsinsDisplay
    },
    usageMarketplacesCountDisplay: function () {
      var pc = this.periodChangesParsed
      if (pc.marketplacesCoveredCount != null && !isNaN(Number(pc.marketplacesCoveredCount))) {
        return this.formatUsageCount(pc.marketplacesCoveredCount)
      }
      var accounts = (this.orderVolumeData && this.orderVolumeData.connectedAccounts) || []
      var seen = {}
      accounts.forEach(function (a) {
        ;(a.marketplaces || []).forEach(function (mp) {
          if (mp) seen[String(mp).toUpperCase()] = true
        })
      })
      var n = Object.keys(seen).length
      if (n > 0) return String(n)
      if (pc.connectedStorefrontsCount != null) return this.formatUsageCount(pc.connectedStorefrontsCount)
      return this.orderVolumeConnectedCountDisplay
    },
    usageChecksRunDisplay: function () {
      var pc = this.periodChangesParsed
      var n =
        pc.checksRunCount != null && !isNaN(Number(pc.checksRunCount))
          ? pc.checksRunCount
          : pc.watchdogChangesCount
      if (n == null || isNaN(Number(n))) return '—'
      return this.formatUsageCount(n)
    },
    usageChecksPerDayLine: function () {
      var pc = this.periodChangesParsed
      var checks =
        pc.checksRunCount != null && !isNaN(Number(pc.checksRunCount))
          ? Number(pc.checksRunCount)
          : Number(pc.watchdogChangesCount)
      if (isNaN(checks) || checks <= 0) return ''
      var start = pc.periodStart
      var end = pc.periodEnd
      if (!start || !end) return ''
      var s = new Date(String(start).indexOf('T') >= 0 ? start : start + 'T12:00:00')
      var e = new Date(String(end).indexOf('T') >= 0 ? end : end + 'T12:00:00')
      if (isNaN(s.getTime()) || isNaN(e.getTime())) return ''
      var days = Math.max(1, Math.round((e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000)) + 1)
      var perDay = Math.round(checks / days)
      return '~' + perDay.toLocaleString('en-US') + ' / day'
    },
    usageCategoriesWithBars: function () {
      var pc = this.periodChangesParsed
      var rows = pc.categories || []
      var max = 0
      rows.forEach(function (row) {
        var c = Number(row.count) || 0
        if (c > max) max = c
      })
      return rows.map(function (row) {
        return {
          key: row.key,
          label: row.label,
          count: row.count,
          countDisplay: Number(row.count).toLocaleString('en-US'),
          barWidth: periodChangesCategoryBarWidth(row.count, max)
        }
      })
    },
    usageAlertDeliveryChannels: function () {
      return ['Email', 'Slack', 'Google Sheets', 'Zapier']
    },
    /** Plan the tenant is on (`starter` | `pro` | `pro_annual` | null). In a trial: the plan billed when it ends. */
    currentPlanKey: function () {
      if (this.isFlatYearlyPlan) return null
      var fromSummary = currentPlanKeyFromSummary(this.subscriptionSummary)
      if (fromSummary) return fromSummary
      if (!this.packageDetails) return null
      var key = String(this.packageDetails.packageKey || this.packageDetails.package_key || '').toUpperCase()
      if (key === 'PRO_ANNUAL') return 'pro_annual'
      if (key === 'PRO') return 'pro'
      if (key === 'STARTER') return 'starter'
      return this.isProPlan ? 'pro' : null
    },
    isProAnnualPlan: function () {
      return this.currentPlanKey === 'pro_annual'
    },
    isInTrialState: function () {
      return this.isTrialBillingActive || this.isTrialBillingExpired
    },
    /** Trial user who picked Pro or Pro Annual. Starter is the default, so it doesn't count as a choice. */
    trialPlanChosen: function () {
      if (!this.isInTrialState) return false
      return this.currentPlanKey === 'pro' || this.currentPlanKey === 'pro_annual'
    },
    planAfterTrialKey: function () {
      if (!this.isInTrialState) return null
      return this.trialPlanChosen ? this.currentPlanKey : 'starter'
    },
    planAfterTrialLabel: function () {
      return this.planDisplayName(this.planAfterTrialKey) || '—'
    },
    planAfterTrialSub: function () {
      if (!this.trialPlanChosen) return 'Unless you choose ' + this.upgradeChoicesLabel + ' below'
      return 'Change anytime before ' + this.nextBillingDateDisplay
    },
    proAnnualPriceCents: function () {
      var fromSummary = this.subscriptionSummary && this.subscriptionSummary.proAnnualPriceCents
      if (fromSummary != null) return fromSummary
      if (this.isProAnnualPlan && this.packageDetails && this.packageDetails.baseSubscriptionFeeCents) {
        return Number(this.packageDetails.baseSubscriptionFeeCents)
      }
      return null
    },
    /** Hidden until PRO_ANNUAL has a Stripe price — always shown to tenants already on it. */
    showProAnnualCard: function () {
      if (this.isProAnnualPlan) return true
      return PRO_ANNUAL_UI_ENABLED && this.proAnnualPriceCents != null
    },
    /** "Pro" or "Pro or Pro Annual" for trial copy, depending on whether the Pro Annual card is offered. */
    upgradeChoicesLabel: function () {
      return this.showProAnnualCard ? 'Pro or Pro Annual' : 'Pro'
    },
    proAnnualPriceDisplay: function () {
      var cents = this.proAnnualPriceCents
      var dollars = cents != null ? cents / 100 : 600
      return Math.floor(dollars) === dollars ? dollars.toLocaleString('en-US') : dollars.toFixed(2)
    },
    proAnnualFootnote: function () {
      if (this.isProAnnualPlan && !this.isInTrialState) return 'Renews ' + this.nextBillingDateDisplay + '.'
      return 'Billed $' + this.proAnnualPriceDisplay + ' up front for 12 months.'
    },
    planSectionHeading: function () {
      if (this.isTrialBillingExpired) return 'Pick a plan to resume service'
      if (this.isTrialBillingActive) return 'Choose your plan for after the trial'
      return 'Change plan'
    },
    planSectionHelp: function () {
      if (this.isTrialBillingExpired) {
        return 'Your data is safe. Pick a plan and add a card to turn your alerts back on.'
      }
      if (this.isTrialBillingActive) {
        if (this.trialPlanChosen) {
          return this.planAfterTrialLabel + ' starts when your trial ends on ' + this.nextBillingDateDisplay +
            '. Nothing is charged before then, and you can switch any time.'
        }
        return 'Nothing is charged during your free trial. Starter starts automatically when it ends unless you choose ' + this.upgradeChoicesLabel + '.'
      }
      return 'Unlimited ASINs and all marketplaces on every plan. Only storefronts affect your price.'
    },
    /** Connected storefronts when known (Usage data), else null. */
    connectedStorefrontCount: function () {
      var fromPeriod = connectedStorefrontsCountFromPeriodChanges(this.periodChangesData)
      if (fromPeriod != null) return fromPeriod
      var accounts = this.orderVolumeData && this.orderVolumeData.connectedAccounts
      return Array.isArray(accounts) ? accounts.length : null
    },
    upgradeModalPlanName: function () {
      return this.planDisplayName(this.upgradeModalPlanKey)
    },
    upgradeModalPriceLabel: function () {
      if (this.upgradeModalPlanKey === 'pro_annual') return '$' + this.proAnnualPriceDisplay + '/year'
      if (this.upgradeModalPlanKey === 'starter') return '$' + STARTER_MONTHLY_PRICE + '/month'
      return '$49/month'
    },
    upgradeModalTitle: function () {
      var name = this.upgradeModalPlanName
      if (this.isInTrialState) return 'Choose ' + name + ' for after your trial?'
      if (this.upgradeModalPlanKey === 'pro_annual' && this.currentPlanKey === 'pro') return 'Switch Pro to yearly billing?'
      return 'Upgrade to ' + name + '?'
    },
    upgradeModalChargeToday: function () {
      if (this.isTrialBillingActive) return '$0 — your free trial continues'
      if (this.upgradeModalPlanKey === 'pro_annual') {
        return '$' + this.proAnnualPriceDisplay + ', minus credit for unused time on your current plan'
      }
      return 'The difference for the rest of this month'
    },
    upgradeModalRenewLabel: function () {
      return this.isTrialBillingActive ? 'First charge' : 'Renews'
    },
    upgradeModalRenewValue: function () {
      if (this.isTrialBillingActive) return this.nextBillingDateDisplay + ' (when your trial ends)'
      return this.upgradeModalPlanKey === 'pro_annual' ? 'Every year from today' : 'Every month on your usual billing date'
    },
    upgradeModalNote: function () {
      var notes = []
      var count = this.connectedStorefrontCount
      if (this.upgradeModalPlanKey !== 'starter' && count != null && count > PRO_INCLUDED_STOREFRONTS) {
        notes.push(
          'You have ' + count + ' storefronts. Storefront ' + (PRO_INCLUDED_STOREFRONTS + 1) + ' onward is billed ' +
          (this.upgradeModalPlanKey === 'pro_annual' ? 'yearly at 12× the monthly volume rate.' : 'monthly at the volume rates.')
        )
      }
      if (this.upgradeModalPlanKey === 'starter' && this.isInTrialState && count != null && count > 2) {
        notes.push('Starter includes 2 storefronts. Your ' + count + ' connected storefronts keep working, but you can’t add more.')
      }
      if (this.upgradeModalPlanKey === 'pro_annual' && !this.isInTrialState) {
        notes.push('If your card is declined, you stay on your current plan.')
      }
      return notes.join(' ')
    },
    upgradeModalConfirmLabel: function () {
      if (this.isInTrialState) return 'Choose ' + this.upgradeModalPlanName
      if (this.upgradeModalPlanKey === 'pro_annual') return 'Pay & switch'
      return 'Confirm upgrade'
    },
    upgradeModalNoPaymentTail: function () {
      if (this.isTrialBillingActive) {
        return 'Add a card to choose it — nothing is charged until your trial ends on ' + this.nextBillingDateDisplay + '.'
      }
      return 'Add a card to continue. You’re only charged when you confirm the switch.'
    },
    trialDaysRemaining: function () {
      if (this.isTrialBillingExpired) return 0
      if (!this.isTrialBillingActive) return null
      var epoch = this.trialPeriodEndEpoch
      if (epoch == null) return null
      var now = Date.now()
      var endMs = Number(epoch) * 1000
      if (isNaN(endMs)) return null
      var diff = endMs - now
      return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)))
    },
    trialDaysRemainingDisplay: function () {
      var n = this.trialDaysRemaining
      if (n == null) return '—'
      return String(n)
    },
    trialLengthDays: function () {
      var start = this.periodStart
      var end = this.trialPeriodEndEpoch
      if (start != null && end != null) {
        var startNum = Number(start)
        var endNum = Number(end)
        if (!isNaN(startNum) && !isNaN(endNum) && endNum > startNum) {
          var days = Math.round((endNum - startNum) / 86400)
          if (days >= 1) return days
        }
      }
      return 14
    },
    trialDayOfDisplay: function () {
      var total = this.trialLengthDays
      var rem = this.trialDaysRemaining
      if (rem == null) return 'Day — of ' + total
      var day = Math.max(1, Math.min(total, total - rem + 1))
      return 'Day ' + day + ' of ' + total
    },
    trialProgressWidth: function () {
      var total = this.trialLengthDays
      var rem = this.trialDaysRemaining
      if (rem == null) return '0%'
      var day = Math.max(1, Math.min(total, total - rem + 1))
      var pct = ((day - 1) / total) * 100
      return Math.max(0, Math.min(100, pct)).toFixed(1) + '%'
    }
  },
  mounted: function () {
    var self = this
    this.handlePaymentMethodReturn()
    $('#sbsPlanChangeModal').modal({ autofocus: false })
    $('#sbsBillingEmailModal').modal({ autofocus: false })
    $('#sbsAchDetailsModal').modal({ autofocus: false })
  },
  methods: {
    normalizeBillingScreenVariant: function (raw) {
      return resolveBillingScreenVariant({
        hasDailyPricing: this.hasDailyPricing,
        packageDetails: this.packageDetails,
        billingUserTypeRaw: raw,
        billingSummary: this.billingSummary
      })
    },
    wirePaymentPopups: function () {
      var self = this
      this.$nextTick(function () {
        var addEl = $('#sbsPaymentMethodsAdd')
        var popupEl = $('#sbsPayPopupAdd')
        if (!addEl.length || !popupEl.length) return
        try {
          addEl.popup('destroy')
        } catch (e) {}
        addEl.popup({ on: 'click', popup: popupEl, position: 'bottom left' })
        var $payModal = $('#sbsPaymentElementModal')
        if ($payModal.length) {
          try {
            $payModal.modal('destroy')
          } catch (e) {}
          $payModal.modal({
            closable: true,
            onHide: function () {
              self.endBillingPaymentModalFlow()
              return true
            }
          })
        }
      })
    },
    startBillingPaymentModalFlow: function () {
      if (this.$root) this.$root.billingPaymentModalFlowActive = true
    },
    endBillingPaymentModalFlow: function () {
      if (this.$root) {
        this.$root.billingPaymentModalFlowActive = false
        this.$root.pendingOpenBillingPaymentModal = false
      }
    },
    openPlanChangeModal: function () {
      if (!this.canManageBilling) return
      this.planChangeMailtoOverride = ''
      $('#sbsPlanChangeModal').modal('show')
    },
    openBillingEmailModal: function () {
      if (!this.canManageBilling) return
      $('#sbsBillingEmailModal').modal('show')
    },
    openPrimaryPaymentUpdate: function () {
      if (!this.canManageBilling) return
      this.startBillingPaymentModalFlow()
      if (this.paymentMethod && this.paymentMethod.method === 'ach') {
        this.viewAchDetails()
        return
      }
      this.paymentModalTitle = this.hasPaymentDetails ? 'Update card' : 'Add card'
      this.initPaymentElement()
    },
    openStripeWidgetFromAdd: function () {
      try {
        $('#sbsPaymentMethodsAdd').popup('hide')
      } catch (e) {}
      this.stripeButtonClicked = true
      this.paymentModalTitle = 'Add payment method'
      this.initPaymentElement()
    },
    createAchFromAdd: function () {
      try {
        $('#sbsPaymentMethodsAdd').popup('hide')
      } catch (e) {}
      this.createAchSource()
    },
    timezoneOffsetMinutes: function () {
      return 0 - new Date().getTimezoneOffset()
    },
    scrollToPaymentMethod: function () {
      this.handleBillingOpenPaymentModal()
    },
    fetchAll: function () {
      var self = this
      this.loading = true
      this.loadError = null
      this.nextBillingFromInvoices = null
      this.$http.get('/api/identity/settings').then(function (res) {
        var body = res.body || {}
        self.isOwner = !!(body.isOwner != null ? body.isOwner : body.is_owner)
        self.tenantId = tenantIdFromIdentityBody(body)
        self.hasDailyPricing = !!body.dailyPricing
        self.periodEndEpoch = body.periodEnd
        self.periodStart = body.periodStart
        self.periodEnd = body.periodEnd && body.periodEnd !== 0 ? self.formatPeriodEndDate(body.periodEnd) : '—'
        self.billingUserTypeRawFromIdentity =
          body.subscriptionBillingUserType ||
          body.user_type ||
          body.billingUserType ||
          body.billing_user_type ||
          null
        if (self.$root) {
          self.$root.tenantId = self.tenantId
          self.$root.billingUserTypeRaw = self.billingUserTypeRawFromIdentity
        }
        if (self.$root && self.$root.loadSubscriptionSummary) {
          self.$root.loadSubscriptionSummary()
        }
        self.loadBilling()
        self.loadInvoices()
        if (self.hasDailyPricing) {
          self.dailyCostBreakdown()
        }
      }, function (err) {
        self.loading = false
        self.loadError = (err.body && err.body.message) || 'Failed to load'
      })
    },
    applySubscriptionSummary: function (body) {
      var summary = normalizeSubscriptionSummary(body)
      this.subscriptionSummary = summary
      if (this.$root) {
        this.$root.subscriptionSummary = summary
        this.$root.$emit('subscription-summary-updated', summary)
      }
      if (!summary) return
      if (summary.cancelAtPeriodEnd) {
        this.subscriptionCanceled = true
      } else if (isSubscriptionStatusActive(summary) || isSubscriptionStatusPastDue(summary)) {
        this.subscriptionCanceled = false
      }
      var epoch = subscriptionEndEpochFromSummary(summary)
      if (epoch != null) {
        this.periodEndEpoch = epoch
        this.periodEnd = this.formatPeriodEndDate(epoch)
      }
      if (summary.paymentInfoAdded) this.hasPaymentDetails = true
    },
    applyPaymentStatusResponse: function (body) {
      body = body || {}
      this.paymentStatusMessage = body.message
      this.subscriptionCanceled = body.canceled
      if (this.$root && body.paymentStatus != null) {
        this.$root.paymentStatus = body.paymentStatus
      }
      this.packageDetails = body.packageDetails
      if (this.$root) this.$root.billingPackageDetails = this.packageDetails
      this.addonCount = body.addonCount
      this.hasPaymentDetails = !!body.hasPaymentDetails
      this.paymentMethod = body.paymentMethod
      var bsRaw = body.billingSummary || body.billing_summary || {}
      var bs = Object.assign({}, this.billingSummary || {}, bsRaw)
      if (!bs.userType && !bs.user_type && (body.user_type || body.userType)) {
        bs.userType = body.user_type || body.userType
      }
      if (body.trackedAsinsCount != null || body.tracked_asins_count != null) {
        bs.trackedAsinsCount =
          body.trackedAsinsCount != null ? body.trackedAsinsCount : body.tracked_asins_count
      }
      this.billingSummary = bs
    },
    finishBillingLoad: function () {
      var self = this
      self.loading = false
      self.$nextTick(function () {
        if (self.canManageBilling) self.wirePaymentPopups()
        if (self.billingVariant === 'daily_usage' && !self.hasDailyPricing) {
          self.dailyCostBreakdown()
        }
        self.maybePrefetchOrderVolume()
        self.maybeCompletePendingUpgrade()
        self.tryFulfillUpgradeRouteIntent()
        self.tryApplyPlanQuerySelection()
        self.maybeOpenPendingPaymentModal()
      })
    },
    maybeOpenPendingPaymentModal: function () {
      if (!this.$root || !this.$root.pendingOpenBillingPaymentModal) return
      this.handleBillingOpenPaymentModal()
    },
    handleBillingOpenPaymentModal: function () {
      if (!this.canManageBilling) {
        this.endBillingPaymentModalFlow()
        return
      }
      this.startBillingPaymentModalFlow()
      var self = this
      if (this.loading) {
        var unwatch = this.$watch('loading', function (val) {
          if (!val) {
            if (typeof unwatch === 'function') unwatch()
            self.$root.pendingOpenBillingPaymentModal = false
            self.activeTab = 'plans'
            self.openPrimaryPaymentUpdate()
          }
        })
        return
      }
      if (this.$root) this.$root.pendingOpenBillingPaymentModal = false
      this.activeTab = 'plans'
      this.openPrimaryPaymentUpdate()
    },
    /** Tenant billing from Stripe (same for owner and team members); endpoints load in parallel. */
    loadBilling: function () {
      var self = this
      var tzParams = { timezoneOffsetMinutes: this.timezoneOffsetMinutes() }
      var summaryPromise = this.$http.get('/api/billing/subscription/summary').catch(function () {
        return { body: null }
      })
      var paymentPromise = this.$http.get('/api/billing/payment-status', { params: tzParams }).catch(function () {
        return { body: null, _failed: true }
      })
      var dailyPromise = this.$http.get('/api/billing/daily-billing-status', { params: tzParams }).catch(function () {
        return { body: null }
      })
      var pmListPromise = this.isOwner
        ? this.$http
          .get('/api/billing/payment-methods')
          .then(function (res) {
            return { paymentMethodsOk: true, body: res.body }
          })
          .catch(function () {
            return { paymentMethodsOk: false, body: null }
          })
        : Promise.resolve({ paymentMethodsOk: false, body: null })

      Promise.all([paymentPromise, dailyPromise, pmListPromise, summaryPromise]).then(function (results) {
        var paymentResponse = results[0]
        var dailyResponse = results[1]
        var pmResponse = results[2]
        var summaryResponse = results[3]

        if (paymentResponse.body && !paymentResponse._failed) {
          self.applyPaymentStatusResponse(paymentResponse.body)
        }
        if (dailyResponse && dailyResponse.body) {
          self.billingSummary = self.mergeDailyBillingStatusSummary(self.billingSummary || {}, dailyResponse.body)
        }
        if (pmResponse && pmResponse.paymentMethodsOk) {
          self.applyPaymentMethodsResponse(pmResponse.body)
        }
        if (summaryResponse && summaryResponse.body) {
          self.applySubscriptionSummary(summaryResponse.body)
        }
        if (self.$root) {
          self.$root.billingSummary = self.billingSummary
          if (!self.$root.billingUserTypeRaw && self.billingUserTypeRawFromIdentity) {
            self.$root.billingUserTypeRaw = self.billingUserTypeRawFromIdentity
          }
        }

        var hasSnapshot = !!(
          self.packageDetails ||
          self.subscriptionSummary ||
          (self.billingSummary && Object.keys(self.billingSummary).length > 0)
        )
        if (paymentResponse._failed && !hasSnapshot) {
          self.loadError = 'Could not load account billing'
          self.loading = false
          return
        }
        self.finishBillingLoad()
      }).catch(function () {
        self.loading = false
        self.loadError = 'Failed to load billing'
      })
    },
    /** Overlay fields from GET /api/billing/daily-billing-status onto billingSummary (userType, amounts, etc.). */
    mergeDailyBillingStatusSummary: function (base, dailyBody) {
      if (!dailyBody || typeof dailyBody !== 'object') return base
      var overlay = dailyBody.billingSummary || dailyBody.billing_summary
      if (overlay && typeof overlay === 'object') {
        return Object.assign({}, base, overlay)
      }
      return Object.assign({}, base, dailyBody)
    },
    extractPaymentMethodsArray: function (body) {
      if (body == null) return []
      if (Array.isArray(body)) return body
      if (typeof body !== 'object') return []
      var arr =
        body.paymentMethods ||
        body.payment_methods ||
        body.methods ||
        body.items ||
        body.data
      return Array.isArray(arr) ? arr : []
    },
    normalizeSavedPaymentMethodRow: function (row) {
      if (!row || typeof row !== 'object') return null
      var method = (row.method || row.type || '')
        .toString()
        .toLowerCase()
        .replace(/-/g, '_')
      var isDefault = !!(row.isDefault || row.default || row.is_default || row.primary)
      if (method === 'card' || row.card) {
        var d = row.details || row.card || {}
        var brand = (row.brand != null ? row.brand : d.brand || d.cardBrand || 'Card').toString()
        var short = brand.length <= 4 ? brand.toUpperCase() : brand.slice(0, 4).toUpperCase()
        var last4 = (row.last4 != null ? row.last4 : d.last4 || d.lastFour || '••••').toString()
        var expM =
          row.exp_month != null
            ? row.exp_month
            : row.expMonth != null
              ? row.expMonth
              : d.exp_month != null
                ? d.exp_month
                : d.expMonth
        var expY =
          row.exp_year != null
            ? row.exp_year
            : row.expYear != null
              ? row.expYear
              : d.exp_year != null
                ? d.exp_year
                : d.expYear
        var prettyBrand = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
        var metaParts = ['Brand: ' + prettyBrand, 'Last 4: ' + last4]
        var expDisp = this.formatCardExpiryDisplay(expM, expY)
        if (expDisp) metaParts.push('Expires: ' + expDisp)
        var created = d.created || row.createdAt || row.created
        if (created) {
          var cd = typeof created === 'number' ? new Date(created * 1000) : new Date(created)
          if (!isNaN(cd.getTime())) {
            metaParts.push(
              'Added ' +
                cd.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
            )
          }
        }
        var label = prettyBrand + ' •••• ' + last4
        return {
          kind: 'card',
          id: row.id || row.payment_method_id || row.paymentMethodId,
          brandShort: short,
          label: label,
          metaLine: metaParts.join(' · ') || 'Card on file',
          isDefault: isDefault
        }
      }
      if (
        method === 'ach' ||
        method === 'us_bank_account' ||
        row.us_bank_account ||
        row.ach
      ) {
        var bank = row.details || row.us_bank_account || row.ach || {}
        var bankName = (bank.bank_name || bank.bankName || '').toString()
        var last4b = bank.last4 || bank.lastFour || ''
        var label =
          bankName && last4b
            ? bankName + ' · •••• ' + last4b
            : bankName
              ? bankName + ' · •••• ' + (last4b || '••••')
              : 'Bank transfer (ACH)'
        return {
          kind: 'ach',
          id: row.id || row.payment_method_id || row.paymentMethodId,
          brandShort: 'ACH',
          label: label,
          metaLine: 'Bank account on file',
          isDefault: isDefault
        }
      }
      return null
    },
    /** MM/YYYY when year is 4+ digits, otherwise MM/YY (matches Stripe-style payloads). */
    formatCardExpiryDisplay: function (expM, expY) {
      if (expM == null || expY == null || expM === '' || expY === '') return ''
      var m = Number(expM)
      var yNum = Number(expY)
      if (isNaN(m) || isNaN(yNum) || m < 1 || m > 12) return ''
      var padM = m < 10 ? '0' + m : String(m)
      if (yNum >= 1000) return padM + '/' + yNum
      var yStr = String(expY)
      var fullYear = yStr.length === 2 ? 2000 + yNum : yNum
      return padM + '/' + String(fullYear).slice(-2)
    },
    applyPaymentMethodsResponse: function (body) {
      var raw = this.extractPaymentMethodsArray(body)
      var list = []
      for (var i = 0; i < raw.length; i++) {
        var normalized = this.normalizeSavedPaymentMethodRow(raw[i])
        if (normalized) list.push(normalized)
      }
      if (
        list.length === 0 &&
        body &&
        typeof body === 'object' &&
        !Array.isArray(body)
      ) {
        var single = this.normalizeSavedPaymentMethodRow(body)
        if (single) list.push(single)
      }
      this.savedPaymentMethods = list
    },
    loadInvoices: function () {
      var self = this
      this.$http.get('/api/billing/invoices', { params: { timezoneOffsetMinutes: this.timezoneOffsetMinutes() } }).then(function (response) {
        var body = response.body || {}
        self.invoices = body.invoices || []
        var nb = body.nextBilling != null ? body.nextBilling : body.next_billing
        self.nextBillingFromInvoices =
          nb != null && String(nb).trim() !== '' ? String(nb).trim() : null
      }, function () {
        self.nextBillingFromInvoices = null
      })
    },
    formatPeriodEndDate: function (timestamp) {
      var date = new Date(timestamp * 1000)
      date.setDate(date.getDate() + 1)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatInvoiceDate: function (fromStr) {
      if (!fromStr) return '—'
      var d = new Date(fromStr)
      if (isNaN(d.getTime())) return fromStr
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    invoicePaidOn: function (inv) {
      return inv.paidOn || inv.to || inv.from
    },
    invoiceDescription: function (inv, idx) {
      var d = this.formatInvoiceDate(this.invoicePaidOn(inv))
      if (this.billingVariant === 'legacy') return this.legacyPlanTitle + ' — ' + d
      if (this.billingVariant === 'daily_usage') return this.dailyPlanTitle + ' — ' + d
      var plan = this.isFlatYearlyPlan ? 'Flat Yearly Subscription' : (this.planDisplayName(this.currentPlanKey) || (this.isProPlan ? 'Pro' : 'Starter'))
      return plan + ' Plan — ' + d
    },
    downloadAllInvoices: function () {
      var self = this
      this.invoices.forEach(function (inv, i) {
        if (inv.url) {
          setTimeout(function () { window.open(inv.url, '_blank') }, i * 400)
        }
      })
    },
    dailyCostBreakdown: function () {
      var self = this
      this.calculatedTotalDailyCost = 0
      this.forcastedAmount = 0
      this.$http.get('/api/billing/daily-cost-breakdown', {
        params: { lastDays: false, from: this.periodStart, to: this.periodEndEpoch }
      }).then(function (response) {
        self.dailyCostBreakdownData = JSON.parse(JSON.stringify(response.body.dailyCostBreakdowns || []))
        var mainLen = self.dailyCostBreakdownData.length
        self.showForecastedColumn = mainLen <= 30
        var lastDay = self.dailyCostBreakdownData[mainLen - 1]
        if (lastDay && self.showForecastedColumn) {
          self.forcastedAmount = (lastDay.amount / 100) * (30 - mainLen)
        }
        var loopRange = mainLen <= 30 ? 30 : mainLen
        for (var i = 0; i < loopRange; i++) {
          if (self.dailyCostBreakdownData[i]) {
            self.calculatedTotalDailyCost += parseFloat((self.dailyCostBreakdownData[i].amount / 100).toFixed(2))
          }
        }
        if (!self.showForecastedColumn) self.forcastedAmount = 0
      }, function () { /* ignore */ })
    },
    planPriceLabel: function (key) {
      if (key === 'pro_annual') return '$' + this.proAnnualPriceDisplay + ' / year'
      if (key === 'pro') return '$49 / mo'
      return '$' + STARTER_MONTHLY_PRICE + ' / mo'
    },
    planDisplayName: function (key) {
      return { starter: 'Starter', pro: 'Pro', pro_annual: 'Pro Annual' }[key] || ''
    },
    /**
     * Ribbon, highlight and button for one plan card.
     * Trial: no plan is "current". Starter is the default after the trial and Pro is recommended,
     * until the user picks Pro or Pro Annual — then that card is the plan after trial and nothing is recommended.
     * Paid: the current plan is marked; higher plans are upgrades; lower ones go through support.
     */
    planCard: function (key) {
      var name = this.planDisplayName(key)
      var current = this.currentPlanKey
      var card = { ribbon: '', ribbonTone: 'orange', current: false, selected: false, recommended: false, label: '', disabled: false, primary: false, title: '' }

      if (this.isInTrialState) {
        var verb = this.isTrialBillingExpired ? 'Resume on ' : (this.trialPlanChosen ? 'Switch to ' : 'Choose ')
        if (this.trialPlanChosen && key === current) {
          card.selected = true
          card.ribbon = 'Plan after trial'
          if (this.isTrialBillingExpired) {
            card.label = 'Resume on ' + name + ' →'
            card.primary = true
          } else {
            card.label = '✓ Plan after trial'
            card.disabled = true
          }
        } else if (!this.trialPlanChosen && key === 'starter') {
          if (this.isTrialBillingExpired) {
            card.label = 'Resume on Starter →'
          } else {
            card.label = 'Default after trial'
            card.disabled = true
            card.title = 'Starter starts automatically when your trial ends.'
          }
        } else {
          if (!this.trialPlanChosen && key === 'pro') {
            card.recommended = true
            card.ribbon = '★ Recommended'
            card.primary = true
          }
          card.label = verb + name + ' →'
        }
      } else {
        var rank = { starter: 1, pro: 2, pro_annual: 3 }
        if (key === current) {
          card.current = true
          card.ribbon = 'Current plan'
          card.ribbonTone = 'navy'
          card.label = 'Current plan'
          card.disabled = true
        } else if (current && rank[key] < rank[current]) {
          card.label = 'Downgrade'
          card.disabled = true
          card.title = 'Downgrades aren’t available in the app yet. Email support to change plans.'
        } else {
          card.primary = true
          if (key === 'pro_annual') {
            card.label = current === 'pro' ? 'Switch to yearly billing →' : 'Upgrade to Pro Annual →'
          } else {
            card.label = 'Upgrade to ' + name + ' →'
            if (key === 'pro') card.ribbon = 'Most popular'
          }
          if (this.isPaymentPastDue) {
            card.disabled = true
            card.title = 'Update your payment method before changing plans.'
          }
        }
      }

      if (this.upgradeInProgress || this.selectPlanInProgress) {
        card.disabled = true
        if (this.upgradeModalPlanKey === key && !card.current) card.label = 'Switching…'
      }
      if (!this.isOwner) card.disabled = true
      return card
    },
    planCardClasses: function (key) {
      var card = this.planCard(key)
      return [
        'sbs-plan-card',
        'sbs-plan-' + (key === 'pro_annual' ? 'annual' : key),
        { current: card.current, selected: card.selected, recommended: card.recommended }
      ]
    },
    planButtonClasses: function (key) {
      var card = this.planCard(key)
      return [
        'sbs-btn',
        'sbs-btn-block',
        card.primary ? 'sbs-btn-upgrade' : 'sbs-btn-outline',
        { 'sbs-btn-muted': card.disabled && !card.primary }
      ]
    },
    onPlanAction: function (key) {
      if (key === 'pro_annual' && !this.showProAnnualCard) return
      if (this.planCard(key).disabled) return
      this.openUpgradeModal(key)
    },
    openUpgradeModal: function (planKey) {
      this.upgradeModalPlanKey = this.planDisplayName(planKey) ? planKey : 'pro'
      if (!this.hasPaymentMethodOnFile) {
        if (this.$root) this.$root.pendingUpgradePlan = this.upgradeModalPlanKey
        this.upgradeModalPhase = 'no_payment'
        this.upgradeModalOpen = true
        return
      }
      this.upgradeModalPhase = 'confirm'
      this.upgradeModalOpen = true
    },
    clearPlanQueryParam: function () {
      if (!this.$route.query || !this.$route.query.plan) return
      var q = Object.assign({}, this.$route.query)
      delete q.plan
      this.$router.replace({ path: this.$route.path, query: q, hash: this.$route.hash || '' })
    },
    tryApplyPlanQuerySelection: function () {
      if (this.loading) return
      if (!this.canManageBilling) {
        this.clearPlanQueryParam()
        return
      }
      var plan = this.$route.query && this.$route.query.plan
      if (!this.planDisplayName(plan)) return
      if (!this.isTrialBillingActive && !this.isTrialBillingExpired) {
        this.clearPlanQueryParam()
        return
      }
      this.onPlanAction(plan)
      this.clearPlanQueryParam()
    },
    closeUpgradeModal: function () {
      if (this.upgradeInProgress) return
      this.upgradeModalOpen = false
    },
    onUpgradeModalAddPayment: function () {
      if (this.$root) this.$root.pendingUpgradePlan = this.upgradeModalPlanKey
      this.upgradeModalOpen = false
      var self = this
      this.$nextTick(function () {
        self.openPrimaryPaymentUpdate()
      })
    },
    clearUpgradeQueryParam: function () {
      if (!this.$route.query || !this.$route.query.upgrade) return
      var q = Object.assign({}, this.$route.query)
      delete q.upgrade
      this.$router.replace({ path: this.$route.path, query: q, hash: this.$route.hash || '' })
    },
    tryFulfillUpgradeRouteIntent: function () {
      if (this.loading) return
      var upgrade = this.$route.query && this.$route.query.upgrade
      if (upgrade !== 'pro' || !this.isOwner) return
      if (this.isProPlan) {
        if (this.$root) this.$root.pendingUpgradePlan = null
        this.clearUpgradeQueryParam()
        return
      }
      if (this.hasPaymentMethodOnFile) {
        this.clearUpgradeQueryParam()
        this.upgradeModalPlanKey = 'pro'
        this.upgradeModalPhase = 'confirm'
        this.upgradeModalOpen = true
        return
      }
      if (this.$root) this.$root.pendingUpgradePlan = 'pro'
      this.upgradeModalPlanKey = 'pro'
      this.upgradeModalPhase = 'no_payment'
      this.upgradeModalOpen = true
    },
    maybeCompletePendingUpgrade: function () {
      if (!this.$root || !this.$root.pendingUpgradePlan) return
      if (!this.isOwner) return
      var plan = this.$root.pendingUpgradePlan
      if (plan === this.currentPlanKey && plan !== 'starter') {
        this.$root.pendingUpgradePlan = null
        this.clearUpgradeQueryParam()
        return
      }
      if (plan === 'starter' && this.currentPlanKey === 'starter' && this.subscriptionSummary && this.subscriptionSummary.status === 'active') {
        this.$root.pendingUpgradePlan = null
        this.clearPlanQueryParam()
        return
      }
      if (!this.hasPaymentMethodOnFile) return
      this.$root.pendingUpgradePlan = null
      this.clearUpgradeQueryParam()
      this.clearPlanQueryParam()
      this.upgradeModalOpen = false
      this.upgradeModalPlanKey = plan
      this.confirmUpgradeToPlan(plan)
    },
    confirmUpgradeModal: function () {
      this.confirmUpgradeToPlan(this.upgradeModalPlanKey)
    },
    confirmUpgradeToPlan: function (planKey) {
      var self = this
      planKey = this.planDisplayName(planKey) ? planKey : 'pro'
      var wasInTrial = this.isTrialBillingActive
      if (!this.hasPaymentMethodOnFile) {
        this.upgradeModalPlanKey = planKey
        this.upgradeModalPhase = 'no_payment'
        this.upgradeModalOpen = true
        if (this.$root) this.$root.pendingUpgradePlan = planKey
        return
      }
      this.upgradeInProgress = true
      this.selectPlanInProgress = true
      this.error = ''
      this.$http.put('/api/billing/subscription/upgrade', { plan: planKey }).then(
        function () {
          if (self.$root) {
            self.$root.pendingUpgradePlan = null
          }
          self.upgradeModalOpen = false
          self.clearUpgradeQueryParam()
          self.clearPlanQueryParam()
          var name = self.planDisplayName(planKey)
          self.message = wasInTrial
            ? name + ' will start when your trial ends.'
            : "You're now on the " + name + ' plan.'
          if (self.$root && self.$root.loadSubscriptionSummary) self.$root.loadSubscriptionSummary(true)
          return self.loadBilling()
        },
        function (res) {
          var body = res && res.body
          self.error = (body && body.message) || 'Could not change your plan. Please try again or contact support.'
        }
      ).then(function () {
        setTimeout(function () {
          self.message = ''
        }, 5000)
        self.upgradeInProgress = false
        self.selectPlanInProgress = false
      })
    },
    unmountPaymentElement: function () {
      this.endBillingPaymentModalFlow()
      if (this.paymentElement) {
        try { this.paymentElement.unmount() } catch (e) {}
        this.paymentElement = null
      }
    },
    initPaymentElement: async function () {
      var self = this
      if (this.stripeInstance && !this.paymentElement) {
        this.stripeInstance = null
      }
      if (this.stripeInstance && this.paymentElement) {
        $('#sbsPaymentElementModal').modal('show')
        return
      }
      try {
        var loadStripe = (await import('@stripe/stripe-js')).loadStripe
        var publishableKey = stripePublishableKey()
        this.stripeInstance = await loadStripe(publishableKey)
        this.updatePaymentInProgress = true
        var setupIntentResponse = await this.$http.post('/api/billing/setup-intent/create')
        var clientSecret = setupIntentResponse.body.clientSecret
        var elements = this.stripeInstance.elements({ clientSecret: clientSecret, appearance: { theme: 'stripe' } })
        this.elementsGroup = elements
        this.paymentElement = elements.create('payment')
        this.paymentElement.mount('#sbs-payment-element')
        this.setupIntentClientSecret = clientSecret
        this.updatePaymentInProgress = false
        $('#sbsPaymentElementModal').modal('show')
      } catch (err) {
        console.error(err)
        this.error = 'Failed to load payment form. Please try again.'
        this.updatePaymentInProgress = false
        this.stripeButtonClicked = false
        this.endBillingPaymentModalFlow()
      }
    },
    confirmPaymentMethod: async function () {
      var self = this
      if (!this.stripeInstance || !this.setupIntentClientSecret) {
        this.error = 'Payment form not initialized'
        return
      }
      try {
        this.updatePaymentInProgress = true
        var returnUrl =
          window.location.origin + '/settings/billing?payment_method_set=success'
        if (this.$root && this.$root.pendingUpgradePlan === 'pro') {
          returnUrl += '&upgrade=pro'
        } else if (this.$root && this.$root.pendingUpgradePlan === 'starter') {
          returnUrl += '&plan=starter'
        }
        var result = await this.stripeInstance.confirmSetup({
          elements: this.elementsGroup,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required'
        })
        if (result.error) {
          this.error = result.error.message
          this.updatePaymentInProgress = false
          return
        }
        var pmId =
          result.setupIntent &&
          result.setupIntent.payment_method
        if (pmId) {
          try {
            await this.$http.put('/api/billing/payment-methods/default', { paymentMethodId: pmId })
          } catch (e) {
            console.warn('Could not set new card as default:', e)
          }
        }
        this.updatePaymentInProgress = false
        this.message = 'Payment method has been updated'
        $('#sbsPaymentElementModal').modal('hide')
        this.endBillingPaymentModalFlow()
        this.loadBilling()
        setTimeout(function () { self.message = '' }, 4000)
        if (this.paymentElement) {
          this.paymentElement.unmount()
          this.paymentElement = null
        }
        this.elementsGroup = null
        this.setupIntentClientSecret = null
        this.stripeInstance = null
        this.stripeButtonClicked = false
        this.paymentModalTitle = 'Update payment method'
      } catch (err) {
        this.error = err.message || 'Failed to update payment method'
        this.updatePaymentInProgress = false
      }
    },
    handlePaymentMethodReturn: function () {
      if (!this.canManageBilling) return
      var self = this
      var params = new URLSearchParams(window.location.search)
      if (params.get('payment_method_set') === 'success') {
        if (this.$root) {
          if (params.get('upgrade') === 'pro') {
            this.$root.pendingUpgradePlan = 'pro'
          } else if (params.get('plan') === 'starter') {
            this.$root.pendingUpgradePlan = 'starter'
          }
        }
        var returnedPmId = params.get('setup_intent_payment_method') || ''
        if (returnedPmId) {
          this.$http.put('/api/billing/payment-methods/default', { paymentMethodId: returnedPmId }).catch(function () {})
        }
        this.message = 'Payment method has been updated'
        this.loadBilling()
        setTimeout(function () { self.message = '' }, 4000)
        window.history.replaceState({}, document.title, '/settings/billing')
      }
    },
    removePaymentMethod: function (pm) {
      if (!pm || !pm.id || this.detachInProgress) return
      if (!window.confirm('Remove this payment method from your account?')) return
      var self = this
      this.detachInProgress = true
      this.error = ''
      this.$http.delete('/api/billing/payment-methods/' + encodeURIComponent(pm.id)).then(
        function () {
          self.message = 'Payment method removed'
          self.loadBilling()
          setTimeout(function () {
            self.message = ''
          }, 4000)
          self.detachInProgress = false
        },
        function (res) {
          var body = res && res.body
          self.error = (body && body.message) || 'Could not remove payment method.'
          self.detachInProgress = false
        }
      )
    },
    setPaymentMethodAsDefault: function (pmId) {
      var self = this
      if (this.detachInProgress) return
      this.setDefaultInProgress = true
      this.error = ''
      this.$http.put('/api/billing/payment-methods/default', { paymentMethodId: pmId }).then(
        function () {
          self.message = 'Default payment method updated'
          self.loadBilling()
          setTimeout(function () { self.message = '' }, 4000)
          self.setDefaultInProgress = false
        },
        function (res) {
          var body = res && res.body
          self.error = (body && body.message) || 'Failed to set default payment method.'
          self.setDefaultInProgress = false
        }
      )
    },
    createAchSource: function () {
      var self = this
      this.updatePaymentInProgress = true
      this.$http.put('/api/billing/ach-source').then(function () {
        self.updatePaymentInProgress = false
        self.message = 'Payment method has been updated'
        self.loadBilling()
        setTimeout(function () { self.message = '' }, 4000)
      }, function (response) {
        self.updatePaymentInProgress = false
        self.error = response.body.message
        self.loadBilling()
      })
    },
    viewAchDetails: function () {
      try {
        $('#sbsPaymentMethodsAdd').popup('hide')
      } catch (e) {}
      $('#sbsAchDetailsModal').modal('show')
    },
    resetCancellationSurveyFields: function () {
      this.cancelSurveyReason = ''
      this.cancelSurveyAlertTags = []
      this.cancelSurveyCompetitor = ''
      this.cancelStayAnswer = ''
      this.cancelDetailText = ''
      this.cancelFlowError = ''
    },
    cancelProgressSegClass: function (n) {
      if (n < this.cancelModalStep) return 'sbs-cancel-progress-seg--done'
      if (n === this.cancelModalStep) return 'sbs-cancel-progress-seg--current'
      return ''
    },
    onCancelFooterKeep: function () {
      if (
        this.cancelModalStep === 1 &&
        this.cancelPreview &&
        this.cancelPreview.alreadyScheduledForCancel
      ) {
        this.resumeSubscription({ closeCancelModal: true })
        return
      }
      this.closeCancelWizardIfIdle()
    },
    resumeSubscription: function (opts) {
      var self = this
      var options = opts || {}
      if (this.resumeInProgress) return
      this.resumeInProgress = true
      this.error = ''
      var request = this.$root.resumeSubscription
        ? this.$root.resumeSubscription()
        : this.$http.put(SUBSCRIPTION_RESUME_URL).then(function () {
          if (self.$root.loadSubscriptionSummary) {
            return self.$root.loadSubscriptionSummary()
          }
        })
      request.then(
        function () {
          self.resumeInProgress = false
          self.subscriptionCanceled = false
          if (options.closeCancelModal) {
            self.cancelModalVisible = false
            self.cancelModalStep = 1
          }
          self.message = 'Your subscription is active again.'
          setTimeout(function () {
            self.message = ''
          }, 6000)
          return self.loadBilling()
        },
        function (err) {
          self.resumeInProgress = false
          self.error = (err.body && err.body.message) || 'Could not resume subscription. Please try again.'
        }
      )
    },
    onCancelFooterContinue: function () {
      if (this.cancelFooterContinueDisabled) return
      if (this.cancelModalStep === 1) {
        this.goCancelStep(2)
        return
      }
      if (this.cancelModalStep === 2) {
        this.onCancelSaveOfferNo()
        return
      }
      if (this.cancelModalStep === 3) {
        this.goCancelStep(4)
        return
      }
      if (this.cancelModalStep === 4) {
        this.submitCancellationFlow()
      }
    },
    fetchCancelSubscriptionPreview: function () {
      var self = this
      this.cancelPreviewLoading = true
      this.cancelPreviewError = ''
      this.$http
        .get('/api/billing/subscription/cancel/preview', {
          params: { timezoneOffsetMinutes: self.timezoneOffsetMinutes() },
        })
        .then(
          function (res) {
            self.cancelPreview = res.body || res.data || null
            self.cancelPreviewLoading = false
          },
          function (res) {
            var msg = res && res.body && res.body.message
            self.cancelPreviewError = msg || 'Unable to load cancellation details.'
            self.cancelPreviewLoading = false
          }
        )
    },
    openCancelWizard: function () {
      if (!this.isOwner) return
      this.planChangeMailtoOverride = ''
      this.resetCancellationSurveyFields()
      this.cancelSaveOfferReached = false
      this.cancelSaveOutcome = null
      this.cancelModalStep = 1
      this.cancelModalVisible = true
      this.fetchCancelSubscriptionPreview()
    },
    closeCancelWizardIfIdle: function () {
      if (this.cancelFlowSubmitting) return
      this.cancelModalVisible = false
      this.cancelModalStep = 1
    },
    goCancelStep: function (step) {
      if (this.cancelFlowSubmitting) return
      if (step === 4 && !this.cancelSurveyReason) return
      if (step === 2) {
        this.cancelSaveOfferReached = true
      }
      this.cancelModalStep = step
    },
    onCancelSaveOfferNo: function () {
      if (this.cancelFlowSubmitting) return
      this.cancelSaveOutcome = false
      this.cancelModalStep = 3
    },
    onCancelSaveOfferYes: function () {
      var self = this
      if (this.cancelFlowSubmitting) return
      this.cancelSaveOutcome = true
      this.cancelFlowSubmitting = true
      this.cancelFlowError = ''
      this.$http.post('/api/billing/subscription/cancel/save-offer', { accepted: true }).then(
        function () {
          self.cancelFlowSubmitting = false
          self.cancelModalVisible = false
          self.cancelModalStep = 1
          self.message =
            'Thanks — we received your request. Our team will reach out about a custom plan shortly.'
          setTimeout(function () {
            self.message = ''
          }, 8000)
        },
        function (res) {
          var msg = res && res.body && res.body.message
          self.cancelFlowError =
            msg || 'Could not send your request. Please try again or email support@sentrykit.com.'
          self.cancelFlowSubmitting = false
        }
      )
    },
    submitCancellationFlow: function () {
      var self = this
      if (!this.cancelSurveyReason || this.cancelFlowSubmitting) return
      var stayPayload = null
      if (this.cancelStayAnswer === 'yes') {
        stayPayload = true
      } else if (this.cancelStayAnswer === 'no') {
        stayPayload = false
      }

      var body = {
        cancellationReason: this.cancelSurveyReason,
        detailText: this.cancelDetailText,
        competitor: this.cancelSurveyReason === 'found_alternative' ? this.cancelSurveyCompetitor : '',
        alertTypes:
          this.cancelSurveyReason === 'too_many_alerts' ? [].concat(this.cancelSurveyAlertTags || []) : [],
        stayResponse: stayPayload,
        cancelSaveOfferShown: !!this.cancelSaveOfferReached,
        saveOfferAccepted: this.cancelSaveOfferReached ? this.cancelSaveOutcome : null,
      }

      this.cancelFlowSubmitting = true
      this.cancelFlowError = ''
      this.$http.post('/api/billing/subscription/cancel/complete', body).then(
        function () {
          self.cancelFlowSubmitting = false
          self.cancelModalVisible = false
          self.message = 'Cancellation processed. Watch your inbox for a confirmation email.'
          setTimeout(function () {
            self.message = ''
          }, 6000)
          self.fetchAll()
        },
        function (res) {
          var msg = res && res.body && res.body.message
          self.cancelFlowError = msg || 'Stripe could not complete cancellation — please retry.'
          self.cancelFlowSubmitting = false
        }
      )
    },
    maybePrefetchOrderVolume: function () {
      if (!ORDER_VOLUME_UI_ENABLED) return
      if (this.orderVolumeChecked || this.orderVolumeLoading) return
      if (
        !shouldShowOrderVolumeUi(this.packageDetails, this.orderVolumeData, {
          billingVariant: this.billingVariant,
          subscriptionSummary: this.subscriptionSummary,
          hasDailyPricing: this.hasDailyPricing,
          billingUserTypeRaw: this.billingUserTypeRawFromIdentity,
          billingSummary: this.billingSummary
        })
      ) {
        return
      }
      this.loadUsageTabData({ silent: true })
    },
    loadUsageTabData: function (opts) {
      if (!ORDER_VOLUME_UI_ENABLED) return Promise.resolve()
      var self = this
      var silent = opts && opts.silent
      this.periodChangesLoading = true
      if (!silent) {
        this.periodChangesError = null
      }
      var periodReq = this.$http.get('/api/v1/watchdogs/summary/period_changes', { params: { time_range: 'all_time' } })
      periodReq.then(
        function (res) {
          self.periodChangesData = res.body || {}
          self.periodChangesLoading = false
          self.periodChangesFetched = true
          self.orderVolumeChecked = true
          if (!self.showUsageTab && self.activeTab === 'usage') {
            self.activeTab = 'plans'
          }
          self.applyBillingTabFromRoute()
        },
        function (err) {
          self.periodChangesError = (err.body && err.body.message) || 'Failed to load monitoring summary'
          self.periodChangesData = null
          self.periodChangesLoading = false
          self.periodChangesFetched = true
          self.orderVolumeChecked = true
          if (!self.showUsageTab && self.activeTab === 'usage') {
            self.activeTab = 'plans'
          }
          self.applyBillingTabFromRoute()
        }
      )
      return periodReq
    },
    loadOrderVolume: function (opts) {
      return this.loadUsageTabData(opts)
    },
    formatUsageCount: function (n) {
      if (n == null || n === '' || isNaN(Number(n))) return '—'
      return Number(n).toLocaleString('en-US')
    },
    formatUsageShortDate: function (isoDate) {
      if (!isoDate) return '—'
      var d = new Date(String(isoDate).indexOf('T') >= 0 ? isoDate : isoDate + 'T12:00:00')
      if (isNaN(d.getTime())) return isoDate
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    applyBillingTabFromRoute: function () {
      var tab = this.$route && this.$route.query && this.$route.query.tab
      if (tab === 'usage') {
        if (this.showUsageTab) {
          this.activeTab = 'usage'
        } else if (this.orderVolumeChecked) {
          this.activeTab = 'plans'
        }
        return
      }
      if (tab === 'history' || tab === 'plans') {
        this.activeTab = tab
      }
    },
    formatUsageDateRange: function (start, end) {
      if (!start || !end) return '—'
      var s = new Date(String(start).indexOf('T') >= 0 ? start : start + 'T12:00:00')
      var e = new Date(String(end).indexOf('T') >= 0 ? end : end + 'T12:00:00')
      if (isNaN(s.getTime()) || isNaN(e.getTime())) return start + ' – ' + end
      var sOpts = { month: 'short', day: 'numeric' }
      var eOpts = { month: 'short', day: 'numeric', year: 'numeric' }
      if (s.getFullYear() === e.getFullYear()) {
        return (
          s.toLocaleDateString('en-US', sOpts) +
          ' – ' +
          e.toLocaleDateString('en-US', eOpts)
        )
      }
      return (
        s.toLocaleDateString('en-US', Object.assign({}, sOpts, { year: 'numeric' })) +
        ' – ' +
        e.toLocaleDateString('en-US', eOpts)
      )
    }
  },
  created: function () {
    var self = this
    this.applyBillingTabFromRoute()
    this.fetchAll()
    if (this.$root) {
      this._onSubscriptionResumed = function () {
        self.loadBilling()
      }
      this._onBillingOpenPaymentModal = function () {
        self.handleBillingOpenPaymentModal()
      }
      this.$root.$on('subscription-resumed', this._onSubscriptionResumed)
      this.$root.$on('billing-open-payment-modal', this._onBillingOpenPaymentModal)
    }
  },
  mounted: function () {
    var self = this
    if (!this.$root) return
    this.$root.openBillingPaymentModal = function () {
      self.handleBillingOpenPaymentModal()
    }
    if (this.$root.pendingOpenBillingPaymentModal) {
      this.$nextTick(function () {
        self.maybeOpenPendingPaymentModal()
      })
    }
  },
  beforeDestroy: function () {
    this.endBillingPaymentModalFlow()
    if (this.$root) {
      if (this._onSubscriptionResumed) {
        this.$root.$off('subscription-resumed', this._onSubscriptionResumed)
      }
      if (this._onBillingOpenPaymentModal) {
        this.$root.$off('billing-open-payment-modal', this._onBillingOpenPaymentModal)
      }
      if (this.$root.openBillingPaymentModal) {
        this.$root.openBillingPaymentModal = null
      }
    }
  }
}
</script>

<style scoped>
.sbs { min-height: 400px; }

.sbs-inner {
  max-width: 800px;
  margin: 0 auto;
}

.sbs-loading { display: flex; justify-content: center; padding: 80px; }
.sbs-spinner { width: 36px; height: 36px; border: 3px solid var(--border-light); border-top-color: var(--primary-navy); border-radius: 50%; animation: sbs-spin 0.7s linear infinite; }
@keyframes sbs-spin { to { transform: rotate(360deg); } }
.sbs-error { text-align: center; padding: 48px; }
.sbs-error-title { font-weight: 600; margin: 0 0 8px; }
.sbs-error-msg { color: var(--text-secondary); margin: 0 0 16px; }

.sbs-header { margin-bottom: 2px; }
.sbs-title { font-size: 17px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.025em; margin: 0 0 6px; }
.sbs-subtitle { font-size: 12px; color: var(--text-secondary); margin: 0; line-height: 1.45; max-width: 40em; }

.sbs-tabs { display: flex; border-bottom: 2px solid #e2e8f0; margin: 0 0 28px; gap: 0; }
.sbs-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
}
.sbs-tab:hover { color: #1e293b; }
.sbs-tab.active { color: var(--primary-navy); border-bottom-color: var(--primary-navy); font-weight: 600; }

.sbs-current-plan {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(180deg, #f3f9ff 0%, #eaf4fd 100%);
  border: 1px solid #cce0f5;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  box-shadow: 0 1px 2px rgba(18, 46, 82, 0.04);
}
.sbs-current-icon { width: 38px; height: 38px; border-radius: 8px; background: var(--primary-navy); color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sbs-current-icon svg { width: 18px; height: 18px; }
.sbs-current-main { flex: 1; min-width: 0; }
.sbs-current-name { display: block; font-size: 14px; font-weight: 700; color: var(--text-primary); line-height: 1.25; }
.sbs-current-meta { font-size: 12px; color: var(--text-secondary); }
.sbs-current-next { text-align: right; padding-left: 8px; }
.sbs-current-next-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 2px;
  text-transform: uppercase;
}
.sbs-current-next-date { font-size: 14px; font-weight: 700; color: var(--text-primary); }

.sbs-agency-banner {
  display: flex;
  align-items: center;
  gap: 11px;
  background: #e8f4fc;
  border: 1px solid #cce8fa;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.sbs-agency-icon { color: var(--primary-navy); flex-shrink: 0; display: flex; align-items: center; }
.sbs-agency-icon svg { width: 18px; height: 18px; }
.sbs-agency-text { flex: 1; margin: 0; font-size: 12px; color: #334155; line-height: 1.45; min-width: 180px; }
.sbs-agency-text strong { font-weight: 600; color: var(--text-primary); }
.sbs-btn-compact { padding: 7px 14px; font-size: 12px; font-weight: 600; text-decoration: none; white-space: nowrap; border-radius: 6px; }

.sbs-section-heading { font-size: 13px; font-weight: 700; color: var(--text-primary); margin: 0 0 10px; letter-spacing: -0.01em; }

.sbs-plans-help { font-size: 12px; color: var(--text-secondary); margin: -6px 0 14px; max-width: 46em; line-height: 1.5; }

.sbs-plan-overage { font-size: 11px; color: var(--text-secondary); margin: 0 0 10px; font-style: italic; }

.sbs-plan-card.recommended { border-color: #ea580c; box-shadow: 0 0 0 1px rgba(234, 88, 12, 0.25); }
.sbs-plan-card.selected { border-color: #ea580c; box-shadow: 0 0 0 2px rgba(234, 88, 12, 0.35); }

.sbs-trial-block {
  background: linear-gradient(135deg, #FFF7ED 0%, #FEF1E4 100%);
  border: 1.5px solid #ea580c;
  border-radius: 12px;
  padding: 18px 18px 14px;
  margin: 0 0 14px;
  position: relative;
  overflow: hidden;
}
.sbs-trial-block::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: #ea580c; }
.sbs-trial-hd { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; margin-bottom: 12px; }
.sbs-trial-hd-left { display: flex; align-items: center; gap: 12px; }
.sbs-trial-icon { width: 42px; height: 42px; border-radius: 10px; background: #ea580c; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #fff; }
.sbs-trial-icon svg { width: 20px; height: 20px; }
.sbs-trial-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; background: #ea580c; color: #fff; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
.sbs-trial-pulse-dot { width: 6px; height: 6px; border-radius: 50%; background: #fff; animation: sbs-pulse 1.6s infinite ease-in-out; }
@keyframes sbs-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
.sbs-trial-title { font-size: 16px; font-weight: 800; color: #7c2d12; line-height: 1.3; }
.sbs-trial-days { text-align: right; }
.sbs-trial-days-num { font-size: 28px; font-weight: 800; color: #7c2d12; line-height: 1; }
.sbs-trial-days-lbl { font-size: 10px; color: #9a3412; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 3px; }

.sbs-trial-progress-wrap { margin-bottom: 12px; }
.sbs-trial-progress-meta { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 11px; color: #9a3412; font-weight: 700; gap: 10px; flex-wrap: wrap; }
.sbs-trial-progress { height: 8px; background: rgba(234, 88, 12, 0.2); border-radius: 999px; overflow: hidden; }
.sbs-trial-progress-fill { height: 100%; background: #ea580c; border-radius: 999px; transition: width 0.35s ease; }

.sbs-trial-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; padding: 12px; background: #fff; border-radius: 10px; border: 1px solid rgba(234, 88, 12, 0.25); }
@media (max-width: 750px) { .sbs-trial-stats { grid-template-columns: 1fr; } }
.sbs-trial-stat { display: flex; flex-direction: column; gap: 3px; }
.sbs-trial-stat-lbl { font-size: 10px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
.sbs-trial-stat-val { font-size: 16px; font-weight: 800; color: var(--primary-navy); }
.sbs-trial-stat-sub { font-size: 11px; color: var(--text-secondary); line-height: 1.35; }
.sbs-trial-stat-sub-hint { color: #9a3412; font-weight: 700; }

.sbs-trial-footnote { font-size: 11.5px; color: #9a3412; margin-top: 10px; display: flex; align-items: flex-start; gap: 8px; line-height: 1.5; }
.sbs-trial-footnote svg { width: 14px; height: 14px; flex-shrink: 0; margin-top: 2px; }

.sbs-trial-block-expired {
  background: linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%);
  border-color: #dc2626;
}
.sbs-trial-block-expired::before { background: #dc2626; }
.sbs-trial-block-expired .sbs-trial-icon { background: #dc2626; }
.sbs-trial-badge-expired { background: #dc2626; }
.sbs-trial-block-expired .sbs-trial-title { color: #7f1d1d; }
.sbs-trial-block-expired .sbs-trial-days-num { color: #7f1d1d; }
.sbs-trial-block-expired .sbs-trial-days-lbl { color: #991b1b; }
.sbs-trial-block-expired .sbs-trial-progress-meta { color: #991b1b; }
.sbs-trial-block-expired .sbs-trial-progress { background: rgba(220, 38, 38, 0.2); }
.sbs-trial-block-expired .sbs-trial-progress-fill { background: #dc2626; }
.sbs-trial-block-expired .sbs-trial-footnote { color: #991b1b; }
.sbs-trial-block-expired .sbs-trial-stats { border-color: rgba(220, 38, 38, 0.3); }

.sbs-past-due-cta {
  margin-top: 16px;
  width: 100%;
  max-width: 320px;
}

.sbs-plan-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 14px;
  max-width: 720px;
  margin: 0 0 28px;
}
.sbs-plan-cards-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); max-width: 1080px; }
@media (max-width: 1100px) { .sbs-plan-cards-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); max-width: 720px; } }
@media (max-width: 750px) { .sbs-plan-cards { grid-template-columns: 1fr; max-width: none; } }
.sbs-plan-card {
  position: relative;
  background: var(--bg-white);
  border: 2px solid var(--border-light);
  border-radius: 10px;
  padding: 20px 14px 12px;
  box-shadow: 0 1px 2px rgba(18, 46, 82, 0.05);
}
.sbs-plan-card.current { border-color: var(--primary-navy); }
.sbs-plan-pro { border-color: #fb923c; }
.sbs-plan-annual { border-color: #fdba74; background: linear-gradient(180deg, #fff7ed 0, var(--bg-white) 120px); }
.sbs-plan-card.current.sbs-plan-pro, .sbs-plan-card.current.sbs-plan-annual { border-color: var(--primary-navy); }
.sbs-plan-ribbon {
  position: absolute;
  top: -9px;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 11px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
  white-space: nowrap;
  text-transform: uppercase;
}
.sbs-plan-ribbon-navy { background: var(--primary-navy); }
.sbs-plan-ribbon-orange { background: #ea580c; }
.sbs-plan-name { font-size: 16px; font-weight: 700; margin: 4px 0 3px; color: var(--text-primary); }
.sbs-plan-limit { font-size: 12px; color: var(--text-secondary); margin: 0 0 6px; }
.sbs-plan-for { font-size: 13px; color: var(--text-secondary); margin: 0 0 10px; }
.sbs-plan-incl { font-size: 12px; color: var(--text-secondary); margin: -6px 0 10px; }

/* ── Storefront counter ── */
.sbs-sf-counter { margin-top: 16px; background: var(--bg, #f4f6f8); border-radius: 10px; padding: 14px 16px; }
.sbs-sf-counter-q { font-size: 13px; font-weight: 600; color: var(--text-primary, #1b2733); margin-bottom: 10px; }
.sbs-sf-counter-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.sbs-sf-step {
  width: 36px; height: 36px; border-radius: 8px;
  border: 1px solid var(--border, #e6e9ed); background: #fff;
  font-size: 20px; font-weight: 500; cursor: pointer; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-primary, #1b2733); transition: border-color .15s;
}
.sbs-sf-step:hover:not(:disabled) { border-color: var(--orange, #f26a21); }
.sbs-sf-step:disabled { opacity: .35; cursor: not-allowed; }
.sbs-sf-count { font-size: 22px; font-weight: 700; min-width: 30px; text-align: center; color: var(--text-primary, #1b2733); }
.sbs-sf-hint { font-size: 12px; color: var(--text-secondary, #6b7785); margin-bottom: 12px; }
.sbs-sf-total-box {
  background: #fff; border: 1px solid #f6d8c4; border-radius: 10px;
  padding: 11px 14px; display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 8px;
}
.sbs-sf-total-label { font-size: 13px; color: var(--text-secondary, #6b7785); }
.sbs-sf-total-price { font-size: 26px; font-weight: 700; color: #b5491a; line-height: 1; }
.sbs-sf-total-period { font-size: 13px; font-weight: 400; color: var(--text-secondary, #6b7785); }
.sbs-sf-save { font-size: 12px; color: var(--green, #1f9d6b); margin-bottom: 8px; }
.sbs-sf-vol-toggle {
  background: none; border: none; padding: 0;
  font-size: 12px; color: var(--blue, #2c6fb5); cursor: pointer; font-family: inherit;
}
.sbs-sf-vol-toggle:hover { text-decoration: underline; }
.sbs-sf-vol-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 8px; }
.sbs-sf-vol-table th { padding: 4px 0; font-weight: 600; color: var(--text-secondary, #6b7785); text-align: left; }
.sbs-sf-vol-table td { padding: 3px 0; color: var(--text-primary, #1b2733); }
.sbs-sf-saving { color: var(--green, #1f9d6b); }
.sbs-plan-price { margin: 0 0 10px; }
.sbs-plan-currency { font-size: 13px; font-weight: 600; vertical-align: top; }
.sbs-plan-amount { font-size: 24px; font-weight: 700; line-height: 1.05; letter-spacing: -0.03em; }
.sbs-plan-period { font-size: 12px; color: var(--text-secondary); margin-left: 2px; }
.sbs-btn-block { width: 100%; justify-content: center; margin-bottom: 14px; padding: 9px !important; font-size: 12px !important; }
.sbs-btn-muted { background: var(--bg-light) !important; color: var(--text-secondary) !important; border: 1px solid var(--border-light) !important; cursor: default !important; }
.sbs-btn-upgrade { width: 100%; justify-content: center; background: #ea580c; color: #fff; border: none; padding: 9px; border-radius: var(--radius-btn); font-weight: 600; font-size: 12px; cursor: pointer; }
.sbs-btn-upgrade:hover:not(:disabled) { opacity: 0.92; }
.sbs-btn-upgrade:disabled { opacity: 0.5; cursor: not-allowed; }
.sbs-plan-features { list-style: none; margin: 0; padding: 0; font-size: 12px; color: var(--text-primary); line-height: 1.4; }
.sbs-plan-features li { padding: 4px 0 4px 18px; position: relative; }
.sbs-plan-features li::before { content: '✓'; position: absolute; left: 0; color: var(--status-success); font-weight: 700; font-size: 11px; }
.sbs-plan-plus-label { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; color: var(--text-muted); margin: 12px 0 6px; }
.sbs-plan-footnote { margin-top: 12px; padding: 10px 10px; background: var(--bg-light); border-radius: var(--radius-btn); font-size: 11px; color: var(--text-secondary); line-height: 1.45; }
.sbs-phase-pill { display: inline-block; background: #fef3c7; color: #c2410c; font-size: 9px; font-weight: 700; padding: 3px 7px; border-radius: 20px; vertical-align: middle; }

.sbs-payment-card { background: var(--bg-white); border: 1px solid var(--border-light); border-radius: var(--radius-card); padding: 18px; margin-top: 8px; }
.sbs-pay-trigger { display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; border: 1px solid var(--border-light); border-radius: var(--radius-btn); background: var(--bg-light); cursor: pointer; font-size: 13px; font-weight: 500; }
.sbs-pay-trigger:hover { background: #f3f4f6; }

/* Legacy / daily “Your plan” card */
.sbs-your-plan-card {
  position: relative;
  background: var(--bg-white);
  border: 2px solid var(--primary-navy);
  border-radius: 10px;
  padding: 18px 16px 14px;
  margin-bottom: 14px;
  box-shadow: 0 1px 2px rgba(18, 46, 82, 0.06);
}
.sbs-daily-trial-expired {
  margin-bottom: 18px;
}
.sbs-your-plan-daily { border-color: #2563eb; }
.sbs-your-plan-badge {
  display: inline-block;
  background: var(--primary-navy);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 4px 10px;
  border-radius: 20px;
  margin-bottom: 12px;
  text-transform: uppercase;
}
.sbs-your-plan-daily .sbs-your-plan-badge { background: #1d4ed8; }
.sbs-your-plan-head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}
.sbs-your-plan-head-main { flex: 1; min-width: 200px; }
.sbs-your-plan-title { font-size: 15px; font-weight: 700; margin: 0 0 6px; color: var(--text-primary); }
.sbs-your-plan-desc { font-size: 12px; color: var(--text-secondary); margin: 0; line-height: 1.45; }
.sbs-your-plan-next { text-align: right; }
.sbs-your-plan-next-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.sbs-your-plan-next-date { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.sbs-your-plan-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}
@media (max-width: 560px) { .sbs-your-plan-stats { grid-template-columns: 1fr; } }
.sbs-your-plan-stat {
  background: #f8fafc;
  border-radius: 8px;
  padding: 10px 12px;
}
.sbs-your-plan-stat-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}
.sbs-your-plan-stat-value { font-size: 16px; font-weight: 700; color: var(--text-primary); }
.sbs-your-plan-stat-sub { display: block; font-size: 11px; color: var(--text-secondary); margin-top: 4px; }
.sbs-asin-meter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #e8f4fc;
  border: 1px solid #cce8fa;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  font-size: 12px;
}
.sbs-asin-meter-label { color: var(--text-secondary); font-weight: 500; }
.sbs-asin-meter-value { font-weight: 700; color: var(--primary-navy); }
.sbs-your-plan-foot {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.45;
  margin: 0;
}

.sbs-email-cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: center;
  padding: 12px 16px;
  margin-bottom: 22px;
  background: var(--bg-white);
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.sbs-email-cta:hover { border-color: var(--primary-navy); background: #f8fafc; }

/* Agency / Enterprise bar */
.sbs-enterprise-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  max-width: 720px;
  background: var(--bg-white, #fff);
  border: 1.5px solid var(--border-light, #e2e8f0);
  border-radius: 10px;
  padding: 14px 20px;
  margin: 0 0 20px;
}
.sbs-enterprise-bar-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sbs-enterprise-bar-text strong {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary, #1b2733);
}
.sbs-enterprise-bar-text span {
  font-size: 12px;
  color: var(--text-secondary, #6b7785);
}
.sbs-enterprise-bar-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  background: #ea580c;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 18px;
  border-radius: 8px;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s;
}
.sbs-enterprise-bar-btn:hover { background: #c2410c; }

/* Payment method block — card shell per Variant A mockup */
.sbs-payment-method-section {
  margin-top: 28px;
  padding: 18px;
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-card);
  box-shadow: 0 1px 3px rgba(18, 46, 82, 0.06);
}
.sbs-payment-method-sub {
  font-size: 12px;
  color: var(--text-secondary);
  margin: -4px 0 14px;
  line-height: 1.45;
}
.sbs-pm-list .sbs-pm-display {
  margin-bottom: 10px;
}
.sbs-pm-list .sbs-pm-display:last-of-type {
  margin-bottom: 14px;
}
.sbs-pm-display {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f4f6f8;
  border: 1px solid var(--border-light);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
}
.sbs-pm-brand {
  flex-shrink: 0;
  min-width: 52px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1f71;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  border-radius: 6px;
}
.sbs-pm-bank { background: #475569; letter-spacing: 0; font-size: 10px; }
.sbs-pm-display-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sbs-pm-display-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.sbs-pm-display-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  line-height: 1.35;
}
.sbs-pm-display-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px 12px;
  flex-shrink: 0;
  margin-left: auto;
  text-align: right;
}
.sbs-pm-display-meta { font-size: 11px; color: var(--text-secondary); }
.sbs-pm-default-badge {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #166534;
  background: #d1fae5;
  padding: 3px 8px;
  border-radius: 4px;
}
.sbs-pm-set-default-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;
  font-weight: 600;
  color: var(--primary-navy, #122e52);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.sbs-pm-set-default-link:hover:not(:disabled) { opacity: 0.75; }
.sbs-pm-set-default-link:disabled { opacity: 0.4; cursor: not-allowed; text-decoration: none; }
.sbs-pm-remove-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;
  font-weight: 600;
  color: #dc2626;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.sbs-pm-remove-link:hover:not(:disabled) { opacity: 0.85; }
.sbs-pm-remove-link:disabled { opacity: 0.4; cursor: not-allowed; text-decoration: none; }
.sbs-pm-empty {
  border: 1px dashed var(--border-light);
  border-radius: 10px;
  padding: 18px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
  margin-bottom: 14px;
}
.sbs-pm-empty p { margin: 0; }
.sbs-pm-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 14px;
}
.sbs-btn-with-icon svg { flex-shrink: 0; opacity: 0.95; }
.sbs-pm-add-wrap { cursor: pointer; }
.sbs-billing-email-row {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0 0 10px;
}
.sbs-link-inline {
  background: none;
  border: none;
  padding: 0 0 0 8px;
  color: var(--primary-navy);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
.sbs-link-inline:hover { opacity: 0.85; }

.sbs-modal-copy { font-size: 13px; line-height: 1.5; color: var(--text-primary); }

.sbs-next-bill { font-size: 13px; color: var(--text-secondary); margin: 16px 0; max-width: 560px; }
.sbs-member-billing-stats {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-subtle, #e8eaed);
}
.sbs-member-admin-hint {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 16px 0 0;
  max-width: 560px;
  line-height: 1.5;
}
.sbs-pay-actions { margin-top: 12px; }

.sbs-btn { display: inline-flex; align-items: center; justify-content: center; padding: 8px 18px; border-radius: var(--radius-btn); font-size: 13px; font-weight: 600; cursor: pointer; transition: opacity 0.15s; border: none; text-decoration: none; }
.sbs-btn-primary { background: var(--primary-navy); color: #fff; }
.sbs-btn-outline { background: var(--bg-white); color: var(--text-primary); border: 1px solid var(--border-light); }
.sbs-btn-outline:hover { background: var(--bg-light); }
.sbs-btn-outline.disabled { opacity: 0.5; pointer-events: none; }

.sbs-placeholder-card { background: var(--bg-white); border: 1px dashed var(--border-light); border-radius: var(--radius-card); padding: 48px 24px; text-align: center; }
.sbs-placeholder-title { font-weight: 700; margin: 0 0 8px; }
.sbs-placeholder-text { color: var(--text-secondary); margin: 0; font-size: 14px; }

.sbs-usage-loading { display: flex; justify-content: center; padding: 64px; }
.sbs-usage-error { text-align: center; padding: 48px 24px; }
.sbs-usage-ineligible { margin-top: 4px; }

/* Usage tab — matches billing mockup (ucards / ublock) */
.sbs-usage-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 3px;
  color: var(--text-primary);
}
.sbs-usage-subtitle {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 16px;
}
.sbs-ucards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 8px;
}
.sbs-ucards-inner { margin: 0; }
@media (max-width: 720px) { .sbs-ucards { grid-template-columns: 1fr; } }
.sbs-ucard {
  background: #fff;
  border: 1px solid var(--border-light, #e6e9ed);
  border-radius: 12px;
  padding: 16px 18px;
}
.sbs-ucard-lever {
  border-color: #f6d8c4;
  background: #fdeee4;
}
.sbs-ucard-lever .sbs-uc-val,
.sbs-ucard-lever .sbs-uc-lbl,
.sbs-ucard-lever .sbs-uc-sub { color: #b5491a; }
.sbs-ucard-muted {
  border: 0;
  background: var(--bg-light, #f4f6f8);
}
.sbs-uc-lbl { font-size: 12px; color: var(--text-secondary); }
.sbs-uc-val {
  font-size: 26px;
  font-weight: 700;
  margin: 5px 0 2px;
  color: var(--text-primary);
  line-height: 1.1;
}
.sbs-uc-sub { font-size: 12px; color: var(--text-muted, #9aa6b2); line-height: 1.4; }
.sbs-ublock {
  background: #fff;
  border: 1px solid var(--border-light, #e6e9ed);
  border-radius: 12px;
  padding: 18px 20px;
  margin-top: 16px;
}
.sbs-ublock-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 14px;
  color: var(--text-primary);
}
.sbs-ublock-title-muted {
  font-weight: 400;
  color: var(--text-secondary);
  font-size: 12px;
}
.sbs-usage-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 10px 0;
  font-size: 13px;
}
.sbs-usage-bar-name {
  width: 210px;
  flex-shrink: 0;
  color: #3a4651;
}
@media (max-width: 560px) {
  .sbs-usage-bar { flex-wrap: wrap; }
  .sbs-usage-bar-name { width: 100%; }
}
.sbs-usage-bar-track {
  flex: 1;
  height: 9px;
  background: var(--bg-light, #f4f6f8);
  border-radius: 20px;
  overflow: hidden;
  min-width: 60px;
}
.sbs-usage-bar-fill {
  display: block;
  height: 100%;
  background: var(--orange, #f26a21);
  border-radius: 20px;
  min-width: 0;
}
.sbs-usage-bar-num {
  width: 34px;
  text-align: right;
  font-weight: 700;
  flex-shrink: 0;
}
.sbs-usage-empty {
  font-size: 13px;
  color: var(--text-secondary);
  padding: 4px 0 8px;
}
.sbs-usage-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.sbs-usage-chip {
  font-size: 12px;
  background: #e9f1fa;
  color: #2c6fb5;
  border-radius: 20px;
  padding: 5px 11px;
}
.sbs-usage-reassure {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 14px;
  display: flex;
  gap: 7px;
  align-items: flex-start;
  line-height: 1.45;
}
.sbs-usage-reassure svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--text-muted, #9aa6b2);
}

.sbs-usage-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 22px;
  margin-bottom: 22px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.sbs-uc-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  gap: 12px;
}
.sbs-uc-title { font-size: 14px; font-weight: 700; color: #1e293b; }
.sbs-uc-period {
  font-size: 12px;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 3px 9px;
  border-radius: 6px;
  white-space: nowrap;
}
.sbs-uc-main {
  display: flex;
  align-items: flex-end;
  gap: 7px;
  margin-bottom: 10px;
}
.sbs-uc-num { font-size: 32px; font-weight: 700; line-height: 1; color: #1e293b; letter-spacing: -0.02em; }
.sbs-uc-of { font-size: 14px; color: #94a3b8; margin-bottom: 3px; }
.sbs-uc-lim { font-size: 14px; font-weight: 600; color: #64748b; margin-bottom: 3px; }
.sbs-uc-track {
  height: 7px;
  background: #f1f5f9;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 9px;
}
.sbs-uc-fill {
  height: 100%;
  border-radius: 5px;
  background: linear-gradient(90deg, var(--primary-navy), var(--primary-navy-light));
  transition: width 0.35s ease;
  min-width: 0;
}
.sbs-uc-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.sbs-uc-pct { font-size: 12px; color: #64748b; }
.sbs-uc-pct strong { color: #1e293b; font-weight: 700; }
.sbs-uc-reset { font-size: 11px; color: #94a3b8; }
.sbs-uc-note {
  margin-top: 12px;
  padding: 9px 13px;
  background: #f1f5f9;
  border-radius: 7px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.5;
}
.sbs-uc-note strong { color: #1e293b; font-weight: 700; }

.sbs-uc-trial-label {
  margin-left: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary, #64748b);
}

.sbs-uc-manage {
  margin-top: 16px;
  width: 100%;
  justify-content: center;
}

.sbs-stat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
@media (max-width: 560px) { .sbs-stat-grid { grid-template-columns: 1fr; } }
.sbs-stat-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.sbs-sc-lbl {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 7px;
}
.sbs-sc-val {
  font-size: 26px;
  font-weight: 700;
  line-height: 1;
  color: #1e293b;
  margin-bottom: 3px;
  letter-spacing: -0.02em;
}
.sbs-sc-sub { font-size: 11px; color: #64748b; line-height: 1.4; }
.sbs-sc-track {
  height: 4px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 9px;
}
.sbs-sc-fill {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--primary-navy), var(--primary-navy-light));
  transition: width 0.35s ease;
  min-width: 0;
}

.sbs-history-card { background: var(--bg-white); border: 1px solid var(--border-light); border-radius: var(--radius-card); padding: 18px; box-shadow: 0 1px 3px rgba(18, 46, 82, 0.06); }
.sbs-history-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.sbs-history-title { font-size: 14px; font-weight: 700; margin: 0; color: var(--text-primary); }
.sbs-link-download { display: inline-flex; align-items: center; gap: 8px; background: none; border: none; color: var(--primary-navy); font-size: 13px; font-weight: 600; cursor: pointer; }
.sbs-link-download:hover { text-decoration: underline; }
.sbs-history-table-wrap { overflow-x: auto; }
.sbs-history-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.sbs-history-table th {
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--text-muted);
  padding: 10px 12px;
  background: #f9fafb;
  border-bottom: 1px solid var(--border-light);
  text-transform: none;
}
.sbs-history-table td { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
.sbs-col-amt { text-align: right; }
.sbs-status-paid { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; background: #ecfdf5; color: #166534; font-size: 12px; font-weight: 600; border-radius: 20px; }
.sbs-status-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--status-success); }
.sbs-receipt-link { color: var(--primary-navy); font-weight: 600; text-decoration: none; font-size: 13px; }
.sbs-receipt-link:hover { text-decoration: underline; }
.sbs-empty-invoices { text-align: center; color: var(--text-muted); padding: 32px; }

/* Fixed toast so plan-change results show wherever the user has scrolled. */
.sbs-flash {
  position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%); z-index: 1100;
  display: flex; align-items: center; gap: 12px; max-width: min(560px, calc(100vw - 32px));
  padding: 12px 14px 12px 16px; border-radius: 10px; font-size: 13px; font-weight: 600;
  box-shadow: 0 10px 30px rgba(18, 46, 82, 0.18);
}
.sbs-flash-ok { background: #ecfdf5; color: #166534; border: 1px solid #a7f3d0; }
.sbs-flash-err { background: #fef2f2; color: var(--status-critical); border: 1px solid #fecaca; }
.sbs-flash-close { background: none; border: none; color: inherit; font-size: 18px; line-height: 1; cursor: pointer; padding: 0 2px; opacity: 0.7; }
.sbs-flash-close:hover { opacity: 1; }

/* Minimal upgrade modal (Starter → Pro) */
.sbs-min-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.48);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}
.sbs-min-modal {
  width: 100%;
  max-width: 400px;
  background: var(--bg-white);
  border-radius: 12px;
  padding: 22px 22px 18px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
  border: 1px solid rgba(148, 163, 184, 0.25);
}
.sbs-min-modal-title {
  margin: 0 0 10px;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-primary);
}
.sbs-min-modal-body {
  margin: 0 0 20px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
}
.sbs-min-modal-body strong {
  color: var(--text-primary);
  font-weight: 600;
}
.sbs-min-modal-summary { margin: 0 0 14px; border: 1px solid var(--border-light); border-radius: 8px; overflow: hidden; }
.sbs-min-modal-summary > div { display: flex; justify-content: space-between; gap: 12px; padding: 9px 12px; font-size: 13px; }
.sbs-min-modal-summary > div + div { border-top: 1px solid var(--border-light); }
.sbs-min-modal-summary dt { color: var(--text-secondary); margin: 0; flex-shrink: 0; }
.sbs-min-modal-summary dd { margin: 0; font-weight: 600; color: var(--text-primary); text-align: right; }
.sbs-min-modal-note { font-size: 12px; }
.sbs-min-modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}
.sbs-min-modal-btn {
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}
.sbs-min-modal-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.sbs-min-modal-btn-secondary {
  background: var(--bg-light);
  color: var(--text-primary);
  border: 1px solid var(--border-light);
}
.sbs-min-modal-btn-secondary:hover:not(:disabled) {
  background: #e8eaed;
}
.sbs-min-modal-btn-primary {
  background: #ea580c;
  color: #fff;
}
.sbs-min-modal-btn-primary:hover:not(:disabled) {
  opacity: 0.92;
}

/* Resume subscription banner */
.sbs-resume-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 10px;
  padding: 12px 14px;
  margin: 14px 0 18px;
  flex-wrap: wrap;
}
.sbs-resume-banner-icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #d1fae5;
  color: #047857;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sbs-resume-banner-text {
  flex: 1;
  min-width: 220px;
}
.sbs-resume-banner-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 700;
  color: #065f46;
}
.sbs-resume-banner-desc {
  margin: 0;
  font-size: 12px;
  color: #065f46;
  line-height: 1.45;
}
.sbs-resume-banner-btn {
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  background: #059669;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.sbs-resume-banner-btn:hover:not(:disabled) {
  background: #047857;
}
.sbs-resume-banner-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Cancel subscription banner & wizard */
.sbs-cancel-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff1f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 12px 14px;
  margin: 14px 0 18px;
  flex-wrap: wrap;
}
.sbs-cancel-banner-icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #fee2e2;
  color: #991b1b;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sbs-cancel-banner-text {
  flex: 1;
  min-width: 220px;
}
.sbs-cancel-banner-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 700;
  color: #7f1d1d;
}
.sbs-cancel-banner-desc {
  margin: 0;
  font-size: 12px;
  color: #7f1d1d;
  line-height: 1.45;
}
.sbs-cancel-banner-btn {
  border: 1px solid #f87171;
  background: #fff;
  color: #991b1b;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.sbs-cancel-banner-btn:hover:not(:disabled) {
  background: #fff7f7;
}
.sbs-cancel-banner-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.sbs-cancel-overlay {
  position: fixed;
  inset: 0;
  z-index: 10060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}
.sbs-cancel-sheet {
  position: relative;
  width: 100%;
  max-width: 560px;
  max-height: 92vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 12px;
  padding: 28px 28px 24px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
}
.sbs-cancel-close {
  position: absolute;
  top: 16px;
  right: 18px;
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
}
.sbs-cancel-close:hover:not(:disabled) {
  color: #0f172a;
}
.sbs-cancel-progress {
  display: flex;
  gap: 6px;
  width: 88px;
  margin: 0 0 20px;
}
.sbs-cancel-progress-seg {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
}
.sbs-cancel-progress-seg--done {
  background: #22c55e;
}
.sbs-cancel-progress-seg--current {
  background: #334155;
}
.sbs-cancel-header {
  margin: 0 32px 20px 0;
}
.sbs-cancel-heading {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.25;
}
.sbs-cancel-subtitle {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #64748b;
}
.sbs-cancel-subtitle strong {
  color: #0f172a;
  font-weight: 700;
}
.sbs-cancel-body {
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #64748b;
}
.sbs-cancel-body--survey {
  margin-bottom: 4px;
}
.sbs-cancel-lead {
  margin: 0 0 10px;
  color: var(--text-primary);
}
.sbs-cancel-bullet {
  margin: 0 0 8px;
  padding-left: 14px;
  position: relative;
}
.sbs-cancel-bullet::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #cbd5f5;
  position: absolute;
  left: 2px;
  top: 0.52em;
}
.sbs-cancel-warn-legacy {
  margin: 8px 0 0;
  padding: 8px 10px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  color: #92400e;
}
.sbs-cancel-warn {
  margin: 8px 0 0;
  padding: 8px 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
}
.sbs-cancel-alert {
  margin: 0 0 8px;
  padding: 8px 10px;
  background: #fef2f2;
  border-radius: 8px;
  color: #b91c1c;
  font-size: 13px;
}
.sbs-cancel-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}
.sbs-cancel-actions-stack {
  flex-direction: column;
}
.sbs-cancel-primary {
  width: 100%;
  justify-content: center !important;
}
.sbs-cancel-danger-outline {
  border-color: #f87171 !important;
  color: #991b1b !important;
}
.sbs-cancel-danger-solid {
  border-color: #dc2626 !important;
  color: #fff !important;
  background: #dc2626 !important;
}
.sbs-cancel-radio-list {
  border: none;
  margin: 0 0 12px;
  padding: 0;
}
.sbs-cancel-radio-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 6px;
  font-size: 13px;
  color: var(--text-primary);
  cursor: pointer;
}
.sbs-cancel-radio-row input {
  margin-top: 2px;
}
.sbs-cancel-mini-label {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 4px;
}
.sbs-cancel-field {
  margin: 10px 0;
}
.sbs-cancel-radio-row-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 12px;
  font-size: 13px;
  cursor: pointer;
}
.sbs-cancel-select {
  width: 100%;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid var(--border-light);
  font-size: 13px;
  margin-top: 4px;
}
.sbs-cancel-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid var(--border-light);
  font-size: 13px;
  margin-top: 4px;
  resize: vertical;
}
.sbs-cancel-footnote-small {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 6px;
}
.sbs-cancel-summary {
  margin: 0 0 6px;
  font-size: 13px;
  color: var(--text-secondary);
}
.sbs-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.sbs-cancel-req {
  color: #dc2626;
  font-weight: 700;
}
.sbs-cancel-opt {
  font-weight: 400;
  color: var(--text-muted);
}
.sbs-cancel-field-label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 10px;
}
.sbs-cancel-survey-section {
  margin: 20px 0 0;
}
.sbs-cancel-reason-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 4px;
}
.sbs-cancel-reason-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;
}
.sbs-cancel-reason-card:hover {
  border-color: #cbd5e1;
}
.sbs-cancel-reason-card--selected {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: none;
}
.sbs-cancel-reason-input {
  flex-shrink: 0;
  margin-top: 4px;
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
}
.sbs-cancel-reason-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.sbs-cancel-reason-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}
.sbs-cancel-reason-sub {
  font-size: 13px;
  color: #64748b;
  line-height: 1.35;
}
.sbs-cancel-pills-block {
  margin: 14px 0 16px;
}
.sbs-cancel-pills-heading {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #64748b;
  margin: 0 0 8px;
  text-transform: uppercase;
}
.sbs-cancel-pills-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.sbs-cancel-pills-hint {
  margin: 8px 0 0;
  font-size: 11px;
  color: #94a3b8;
}
.sbs-cancel-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  font-size: 12px;
  cursor: pointer;
  background: #fff;
  color: var(--text-primary);
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.sbs-cancel-pill:hover {
  border-color: #cbd5e1;
}
.sbs-cancel-pill--on {
  background: var(--primary-navy, #122e52);
  border-color: var(--primary-navy, #122e52);
  color: #fff;
}
.sbs-cancel-pill-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.sbs-cancel-segment {
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}
.sbs-cancel-segment-btn {
  min-width: 72px;
  padding: 9px 20px;
  border: none;
  border-right: 1px solid #e5e7eb;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
}
.sbs-cancel-segment-btn:last-child {
  border-right: none;
}
.sbs-cancel-segment-btn--active {
  background: #f1f5f9;
  color: #0f172a;
}
.sbs-cancel-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  line-height: 1.45;
  margin-top: 0;
  min-height: 100px;
  resize: vertical;
  color: #0f172a;
}
.sbs-cancel-textarea::placeholder {
  color: #94a3b8;
}
.sbs-cancel-select {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  margin-top: 0;
  background: #fff;
  color: #0f172a;
}
.sbs-cancel-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 0;
}
.sbs-cancel-footer-keep {
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
}
.sbs-cancel-footer-keep:hover:not(:disabled) {
  background: #f8fafc;
}
.sbs-cancel-footer-continue {
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid #334155;
  background: #f8fafc;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
}
.sbs-cancel-footer-continue:hover:not(:disabled) {
  background: #f1f5f9;
}
.sbs-cancel-footer-continue:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  border-color: #e5e7eb;
  color: #94a3b8;
}
.sbs-cancel-impact-box {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 18px;
}
.sbs-cancel-impact-plain {
  margin: 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
}
.sbs-cancel-impact-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.45;
  color: #334155;
}
.sbs-cancel-impact-row:last-child {
  margin-bottom: 0;
}
.sbs-cancel-impact-icon {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}
.sbs-cancel-impact-icon--ok {
  background: #dcfce7;
  color: #16a34a;
}
.sbs-cancel-impact-icon--warn {
  background: #ffedd5;
  color: #ea580c;
  font-size: 11px;
}
.sbs-cancel-save-offer {
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 8px;
  padding: 20px 18px;
  text-align: center;
}
.sbs-cancel-save-offer-title {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  color: #c2410c;
}
.sbs-cancel-save-offer-text {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: #9a3412;
}
.sbs-cancel-save-offer-yes {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: #ea580c;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.sbs-cancel-save-offer-yes:hover:not(:disabled) {
  background: #c2410c;
}
.sbs-cancel-save-offer-no {
  display: block;
  width: 100%;
  border: none;
  background: none;
  color: #9a3412;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px;
}
.sbs-cancel-final-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 14px;
}
.sbs-cancel-final-box-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #7f1d1d;
}
.sbs-cancel-final-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 6px;
  border-radius: 50%;
  background: #fee2e2;
  color: #dc2626;
  font-size: 12px;
  font-weight: 800;
  vertical-align: middle;
}
.sbs-cancel-final-copy {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #64748b;
}
</style>

<!-- Stripe payment modal must sit above trial/past-due lockout overlays (z-index 1050) -->
<style>
#sbsPaymentElementModal.ui.modal,
.ui.dimmer.modals.visible {
  z-index: 10100 !important;
}
</style>



// WEBPACK FOOTER //
// SubscriptionBillingSettings.vue?e0f50a74